import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { hasGranularPermission } from '../lib/permissions';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import {
  BookOpen,
  Clock,
  Users,
  Award,
  TrendingUp,
  Play,
  FileText,
  Download,
  Star,
  Filter,
  Search,
  Plus,
  Edit,
  Trash2,
  UserCheck,
  Monitor,
  BarChart3,
  Upload,
  Link,
  Video,
  FileImage,
  HelpCircle,
  CheckCircle,
  PlayCircle,
  Upload as UploadIcon,
  Type,
  Headphones,
  Presentation,
  ExternalLink,
  Package,
  AlertCircle,
  Calendar,
  Eye,
  Zap,
  GraduationCap,
  BookmarkPlus,
  X,
  ChevronLeft,
  ChevronRight,
  Shield,
  Save
} from 'lucide-react';

import TrilhasTab from './lms/TrilhasTab';
import ConformidadeTab from './lms/ConformidadeTab';
import ReactionSurveyDialog from './lms/ReactionSurveyDialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Content Type Icons
const CONTENT_ICONS = {
  video: PlayCircle,
  document: FileText,
  presentation: Presentation,
  quiz: HelpCircle,
  text: Type,
  audio: Headphones,
  link: ExternalLink,
  scorm: Package
};

// Level Colors
const LEVEL_COLORS = {
  beginner: 'bg-green-100 text-green-800',
  intermediate: 'bg-yellow-100 text-yellow-800',
  advanced: 'bg-red-100 text-red-800'
};

const ALL_LMS_TABS = [
  { id: 'lms_dashboard', label: 'Dashboard' },
  { id: 'catalog', label: 'Catálogo' },
  { id: 'my-courses', label: 'Meus Cursos' },
  { id: 'trilhas', label: 'Trilhas' },
  { id: 'certificates', label: 'Certificados' },
  { id: 'conformidade', label: 'Conformidade' },
  { id: 'admin', label: 'Administração' },
];

const LMSPage = () => {
  const { user } = useAuth(); // Get current user with profile info
  const location = useLocation(); // Para receber o state de navegação
  
  // Check if user has admin privileges (Administrador or RH profile)
  const hasAdminAccess = user && (
    user.is_admin || 
    user.profile_name === 'Administrador' || 
    user.profile_name === 'RH'
  );

  const [currentTab, setCurrentTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    return tabFromUrl && ALL_LMS_TABS.some(t => t.id === tabFromUrl) ? tabFromUrl : 'lms_dashboard';
  });
  const [loading, setLoading] = useState(false);

  // Available tabs based on granular permissions
  const availableTabs = useMemo(() => {
    return ALL_LMS_TABS.filter(tab =>
      hasGranularPermission(user, 'lms', tab.id)
    ).map(t => t.id);
  }, [user]);

  // Ensure current tab is valid for user permissions
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(currentTab)) {
      setCurrentTab(availableTabs[0]);
    }
  }, [availableTabs, currentTab]);
  
  // Data states
  const [courses, setCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [coursePlayer, setCoursePlayer] = useState({ courseId: null, isOpen: false });
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');


  // Upload states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');

  // Course creation state
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    short_description: '',
    category: '',
    level: 'beginner',
    duration_hours: 0,
    is_mandatory: false,
    tags: [],
    learning_objectives: [''],
    prerequisites: [''],
    competencies: []
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  // Sincronizar tab quando a URL mudar (ex: navegação pelo menu)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && tabFromUrl !== currentTab && ALL_LMS_TABS.some(t => t.id === tabFromUrl)) {
      setCurrentTab(tabFromUrl);
    }
  }, [location.search]);

  // Abrir curso automaticamente quando vier do PDI
  useEffect(() => {
    const openCourseFromPDI = async () => {
      if (location.state?.openCourse && location.state?.selectedCourseId && courses.length > 0) {
        const courseId = location.state.selectedCourseId;
        const course = courses.find(c => c.id === courseId);
        
        if (course) {
          setSelectedCourse(course);
          setCurrentTab('catalog'); // Mudar para a aba de catálogo
          toast.success('Abrindo curso recomendado pelo PDI');
        } else {
          // Se não encontrou nos courses carregados, buscar diretamente
          try {
            const response = await axios.get(`${API}/api/lms/courses/${courseId}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            setSelectedCourse(response.data);
            setCurrentTab('catalog');
            toast.success('Abrindo curso recomendado pelo PDI');
          } catch (error) {
            toast.error('Curso não encontrado');
          }
        }
        
        // Limpar o state para não reabrir ao recarregar
        window.history.replaceState({}, document.title);
      }
    };
    
    openCourseFromPDI();
  }, [location.state, courses]);

  // Ir direto para "Meus Cursos" quando vier de link de e-mail (?courseId=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const courseId = params.get('courseId');
    if (!courseId) return;

    setCurrentTab('my-courses');

    // Limpar o parâmetro da URL para não repetir a navegação
    params.delete('courseId');
    const newSearch = params.toString();
    window.history.replaceState({}, document.title, `${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  }, [location.search]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCourses(),
        loadMyCourses(),
        loadDashboard(),
        loadEmployees()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Erro ao carregar dados iniciais');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/api/lms/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadMyCourses = async () => {
    try {
      const response = await axios.get(`${API}/api/lms/my-courses`);
      setMyCourses(response.data);
    } catch (error) {
      console.error('Error loading my courses:', error);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/lms/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      // Rota enxuta (só ativos, só id/nome/email/cargo/empresa) — é só o que
      // o modal de Atribuir Curso usa; evita buscar o cadastro completo.
      const response = await axios.get(`${API}/api/employees/lite`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const handleEnrollCourse = async (courseId) => {
    try {
      await axios.post(`${API}/api/lms/courses/${courseId}/enroll`);
      toast.success('Matrícula realizada com sucesso!');
      await Promise.all([loadCourses(), loadMyCourses(), loadDashboard()]);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Você já está matriculado neste curso');
      } else {
        toast.error('Erro ao realizar matrícula');
      }
    }
  };

  const handleAutoEnrollFromPDI = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/lms/auto-enroll-from-pdi`);
      toast.success(`${response.data.enrollments_created} matrículas automáticas realizadas!`);
      await Promise.all([loadCourses(), loadMyCourses(), loadDashboard()]);
    } catch (error) {
      toast.error('Erro na matrícula automática');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToEmployees = async (courseId, employeeIds) => {
    try {
      setLoading(true);
      let successCount = 0;
      
      for (const employeeId of employeeIds) {
        try {
          await axios.post(`${API}/api/lms/courses/${courseId}/assign`, {
            user_id: employeeId,
            assigned_by: 'admin',
            notes: 'Atribuído pelo administrador'
          });
          successCount++;
        } catch (error) {
          console.warn('Assignment failed, trying enrollment:', error);
          // Fallback to regular enrollment
          try {
            await axios.post(`${API}/api/lms/courses/${courseId}/enroll`);
            successCount++;
          } catch (enrollError) {
            console.error('Enrollment also failed:', enrollError);
          }
        }
      }
      
      if (successCount > 0) {
        toast.success(`Curso atribuído a ${successCount} funcionário(s)!`);
        await Promise.all([loadCourses(), loadMyCourses(), loadDashboard()]);
      } else {
        toast.error('Não foi possível atribuir o curso');
      }
    } catch (error) {
      console.error('Error assigning course:', error);
      toast.error('Erro ao atribuir curso');
    } finally {
      setLoading(false);
      setSelectedCourse(null);
    }
  };

  const handleAssignCourse = (course) => {
    // employees já foi carregado no mount (loadInitialData); reaproveitar evita
    // um round-trip extra (e o delay perceptível) toda vez que o modal abre.
    setSelectedCourse({ ...course, availableEmployees: employees });
    if (employees.length === 0) {
      loadEmployees();
    }
  };

  // Mantém a lista de funcionários do modal de atribuição em dia caso ele
  // tenha aberto antes do carregamento inicial terminar.
  useEffect(() => {
    setSelectedCourse((prev) =>
      prev && prev.availableEmployees ? { ...prev, availableEmployees: employees } : prev
    );
  }, [employees]);

  const handleAccessCourse = (courseId) => {
    // Navigate to course player/viewer
    setSelectedCourse(null); // Close modal
    setCoursePlayer({ courseId, isOpen: true }); // Open course player
  };


  // Upload file in chunks with progress
  const handleFileUpload = async (file, fileType) => {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
    const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB max
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Arquivo muito grande! Tamanho máximo: 2GB. Tamanho do arquivo: ${(file.size / 1024 / 1024 / 1024).toFixed(2)}GB`);
      return null;
    }

    // Validate file type
    if (fileType === 'video') {
      const videoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov', 'video/quicktime'];
      if (!videoTypes.includes(file.type)) {
        toast.error('Tipo de vídeo inválido! Use MP4, WebM, OGG, AVI ou MOV');
        return null;
      }
    } else if (fileType === 'pdf') {
      if (file.type !== 'application/pdf') {
        toast.error('Tipo de arquivo inválido! Use apenas PDF');
        return null;
      }
    }

    try {
      setIsUploading(true);
      setUploadingFileName(file.name);
      setUploadProgress(0);

      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Upload each chunk
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('chunk_index', chunkIndex);
        formData.append('total_chunks', totalChunks);
        formData.append('upload_id', uploadId);
        formData.append('filename', file.name);
        formData.append('file_type', fileType);

        await axios.post(`${API}/api/lms/upload-chunk`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        // Update progress
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setUploadProgress(progress);
      }

      // Finalize upload
      const finalizeFormData = new FormData();
      finalizeFormData.append('upload_id', uploadId);
      finalizeFormData.append('filename', file.name);
      finalizeFormData.append('total_chunks', totalChunks);
      finalizeFormData.append('file_type', fileType);

      const response = await axios.post(`${API}/api/lms/finalize-upload`, finalizeFormData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success(`Upload concluído! ${file.name}`);
      
      setIsUploading(false);
      setUploadProgress(0);
      setUploadingFileName('');

      return response.data.url; // Return the file URL

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Erro no upload: ${error.response?.data?.detail || error.message}`);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadingFileName('');
      return null;
    }
  };


  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
    const matchesLevel = levelFilter === 'all' || course.level === levelFilter;
    
    return matchesSearch && matchesCategory && matchesLevel;
  });

  const categories = [...new Set(courses.map(c => c.category).filter(Boolean))];
  const levels = ['beginner', 'intermediate', 'advanced'];

  const formatDuration = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    return `${hours}h`;
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: 'Iniciante',
      intermediate: 'Intermediário',
      advanced: 'Avançado'
    };
    return labels[level] || level;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <GraduationCap className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <span>Learning Management System</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Plataforma integrada de aprendizagem e desenvolvimento
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleAutoEnrollFromPDI} disabled={loading}>
            <Zap className="h-4 w-4 mr-2" />
            {loading ? 'Processando...' : 'Matrícula Automática PDI'}
          </Button>
          <Button variant="outline" onClick={loadInitialData}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Access Level Info */}
      {!hasAdminAccess && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500 text-white rounded-full">
                <Users className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-medium text-blue-900">Acesso Limitado</h3>
                <p className="text-sm text-blue-700">
                  Você tem acesso às suas matrículas e certificados. Para gerenciar cursos e acessar o catálogo completo, é necessário perfil Administrador ou RH.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {hasAdminAccess && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-500 text-white rounded-full">
                <Shield className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-medium text-green-900">Acesso Administrativo</h3>
                <p className="text-sm text-green-700">
                  Você tem acesso completo ao LMS, incluindo catálogo de cursos e ferramentas de administração.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
        <TabsList
          className="flex w-full items-center justify-start gap-1 overflow-x-auto sm:grid sm:overflow-x-visible"
          style={{ gridTemplateColumns: `repeat(${Math.min(availableTabs.length, 7)}, minmax(0, 1fr))` }}
        >
          {ALL_LMS_TABS.filter(t => availableTabs.includes(t.id)).map(tab => (
            <TabsTrigger key={tab.id} value={tab.id} className="flex-shrink-0">{tab.label}</TabsTrigger>
          ))}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="lms_dashboard">
          {dashboard && (
            <LMSDashboard 
              dashboard={dashboard}
              onCourseClick={(course) => {
                setSelectedCourse(course);
                setCurrentTab('my-courses');
              }}
            />
          )}
        </TabsContent>

        {/* Catalog Tab - Only for users with permission */}
        {availableTabs.includes('catalog') && (
          <TabsContent value="catalog">
            <div className="space-y-6">
              {/* Filters */}
              <Card>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="search">Buscar Cursos</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="search"
                          placeholder="Digite para buscar..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="category">Categoria</Label>
                      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas as Categorias</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="level">Nível</Label>
                      <Select value={levelFilter} onValueChange={setLevelFilter}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os Níveis</SelectItem>
                          {levels.map(level => (
                            <SelectItem key={level} value={level}>
                              {getLevelLabel(level)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-end">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setSearchTerm('');
                          setCategoryFilter('all');
                          setLevelFilter('all');
                        }}
                      >
                        <Filter className="h-4 w-4 mr-2" />
                        Limpar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Course Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses.map(course => (
                  <CourseCard 
                    key={course.id} 
                    course={course} 
                    onEnroll={handleEnrollCourse}
                    onViewDetails={(course) => setSelectedCourse(course)}
                  />
                ))}
              </div>

              {filteredCourses.length === 0 && (
                <Card>
                  <CardContent className="text-center py-12">
                    <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum curso encontrado com os filtros aplicados</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        )}

        {/* My Courses Tab */}
        <TabsContent value="my-courses">
          <MyCourses 
            courses={myCourses}
            onCourseClick={setSelectedCourse}
            onReload={loadMyCourses}
          />
        </TabsContent>

        {/* Trilhas Tab */}
        <TabsContent value="trilhas">
          <TrilhasTab hasAdminAccess={hasAdminAccess} onOpenCourse={handleAccessCourse} />
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates">
          <CertificatesView 
            certificates={dashboard?.certificates || []}
          />
        </TabsContent>

        {/* Conformidade Tab - Only for users with permission */}
        {availableTabs.includes('conformidade') && (
          <TabsContent value="conformidade">
            <ConformidadeTab />
          </TabsContent>
        )}

        {/* Admin Tab - Only for users with permission */}
        {availableTabs.includes('admin') && (
          <TabsContent value="admin">
            <CourseAdmin 
              onCourseCreated={loadCourses}
              onAssignCourse={handleAssignCourse}
              onEditCourseContent={(course) => setSelectedCourse({ ...course, isContentEdit: true })}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Course Content Editor Modal */}
      {selectedCourse && selectedCourse.isContentEdit && (
        <CourseContentEditor
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
          onSave={loadCourses}
        />
      )}

      {/* Course Detail Modal */}
      {selectedCourse && !selectedCourse.availableEmployees && !selectedCourse.isContentEdit && (
        <CourseDetailModal 
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
          onEnroll={handleEnrollCourse}
          onReload={loadCourses}
          onAccessCourse={handleAccessCourse}
        />
      )}

      {/* Course Assignment Modal */}
      {selectedCourse && selectedCourse.availableEmployees && (
        <CourseAssignmentModal
          course={selectedCourse}
          employees={selectedCourse.availableEmployees}
          onClose={() => setSelectedCourse(null)}
          onAssign={handleAssignToEmployees}
          loading={loading}
        />
      )}

      {/* Course Player */}
      {coursePlayer.isOpen && (
        <CoursePlayer
          courseId={coursePlayer.courseId}
          onClose={() => {
            setCoursePlayer({ courseId: null, isOpen: false });
            loadMyCourses();
            loadDashboard();
          }}
        />
      )}
    </div>
  );
};

// Course Assignment Modal Component
const CourseAssignmentModal = ({ course, employees, onClose, onAssign, loading }) => {
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEmployees = employees.filter(emp => 
    emp.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleAssign = () => {
    if (selectedEmployees.length > 0) {
      onAssign(course.id, selectedEmployees);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Atribuir Curso: {course.title}</CardTitle>
              <CardDescription>Selecione os funcionários para este curso</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onClose}>
              ×
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar funcionário..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Employee List */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {filteredEmployees.map(employee => (
              <div key={employee.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                <input
                  type="checkbox"
                  checked={selectedEmployees.includes(employee.id)}
                  onChange={() => handleEmployeeToggle(employee.id)}
                />
                <div className="flex-1">
                  <p className="font-medium">{employee.nome}</p>
                  <p className="text-sm text-gray-500">{employee.email}</p>
                  <p className="text-xs text-gray-400">{employee.cargo} - {employee.empresa}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-4 border-t">
            <p className="text-sm text-gray-500">
              {selectedEmployees.length} funcionário(s) selecionado(s)
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleAssign}
                disabled={loading || selectedEmployees.length === 0}
              >
                {loading ? 'Atribuindo...' : 'Atribuir Curso'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// LMS Dashboard Component
const LMSDashboard = ({ dashboard, onCourseClick }) => {
  const stats = dashboard.stats;
  
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Cursos</CardTitle>
            <BookOpen className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.total_courses}</div>
            <p className="text-xs text-muted-foreground">
              Cursos matriculados
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cursos Concluídos</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.completed_courses}</div>
            <p className="text-xs text-muted-foreground">
              {stats.completion_rate.toFixed(1)}% de conclusão
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Progresso</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.in_progress_courses}</div>
            <p className="text-xs text-muted-foreground">
              Cursos iniciados
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Certificados</CardTitle>
            <Award className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.total_certificates}</div>
            <p className="text-xs text-muted-foreground">
              Certificados obtidos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>Progresso Geral</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Taxa de Conclusão</span>
                  <span className="text-sm text-gray-600">{stats.completion_rate.toFixed(1)}%</span>
                </div>
                <Progress value={stats.completion_rate} className="h-2" />
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.completed_courses}</div>
                  <div className="text-xs text-gray-600">Concluídos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{stats.in_progress_courses}</div>
                  <div className="text-xs text-gray-600">Em Progresso</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-600">
                    {stats.total_courses - stats.completed_courses - stats.in_progress_courses}
                  </div>
                  <div className="text-xs text-gray-600">Não Iniciados</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>Atividade Recente</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.recent_activity.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Nenhuma atividade recente</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dashboard.recent_activity.slice(0, 5).map((activity, index) => (
                  <div 
                    key={index} 
                    className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    onClick={() => onCourseClick(activity.course)}
                  >
                    <div className="flex-shrink-0">
                      <BookOpen className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="text-sm font-medium truncate">{activity.course.title}</p>
                      <p className="text-xs text-gray-600">
                        Progresso: {activity.enrollment.progress_percentage.toFixed(1)}%
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Badge variant={
                        activity.enrollment.status === 'completed' ? 'default' :
                        activity.enrollment.progress_percentage > 0 ? 'secondary' : 'outline'
                      }>
                        {activity.enrollment.status === 'completed' ? 'Concluído' :
                         activity.enrollment.progress_percentage > 0 ? 'Em Progresso' : 'Não Iniciado'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Course Card Component
const CourseCard = ({ course, onEnroll, onViewDetails }) => {
  const ContentIcon = CONTENT_ICONS[course.content_type] || BookOpen;
  
  const formatDuration = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    return `${hours}h`;
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: 'Iniciante',
      intermediate: 'Intermediário',
      advanced: 'Avançado'
    };
    return labels[level] || level;
  };
  
  return (
    <Card className="group hover:shadow-lg transition-all duration-200 cursor-pointer">
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-grow">
              <h3 className="font-semibold text-lg line-clamp-2 group-hover:text-blue-600 transition-colors">
                {course.title}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2">
                {course.short_description || course.description}
              </p>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Clock className="h-4 w-4" />
              <span>{formatDuration(course.duration_hours)}</span>
            </div>
            
            {course.category && (
              <Badge variant="outline">{course.category}</Badge>
            )}
            
            <Badge className={LEVEL_COLORS[course.level]}>
              {getLevelLabel(course.level)}
            </Badge>
          </div>

          {/* Progress bar if enrolled */}
          {course.is_enrolled && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progresso</span>
                <span>{course.user_progress?.toFixed(1) || 0}%</span>
              </div>
              <Progress value={course.user_progress || 0} className="h-2" />
            </div>
          )}

          {/* Tags */}
          {course.tags && course.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {course.tags.slice(0, 3).map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {course.tags.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{course.tags.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-2 pt-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => onViewDetails(course)}
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-2" />
              Ver Detalhes
            </Button>
            
            {!course.is_enrolled ? (
              <Button 
                size="sm" 
                onClick={() => onEnroll(course.id)}
                className="flex-1"
              >
                <BookmarkPlus className="h-4 w-4 mr-2" />
                Matricular-se
              </Button>
            ) : (
              <Button 
                size="sm" 
                onClick={() => onViewDetails(course)}
                className="flex-1"
              >
                <PlayCircle className="h-4 w-4 mr-2" />
                Continuar
              </Button>
            )}
          </div>

          {/* Mandatory indicator */}
          {course.is_mandatory && (
            <div className="flex items-center space-x-1 text-red-600 text-xs">
              <AlertCircle className="h-3 w-3" />
              <span>Curso Obrigatório</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// My Courses Component
const MyCourses = ({ courses, onCourseClick, onReload }) => {
  const { user } = useAuth(); // Get current user info
  const [statusFilter, setStatusFilter] = useState('all');
  
  const filteredCourses = courses.filter(course => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'completed') return course.enrollment.status === 'completed';
    if (statusFilter === 'in-progress') return course.enrollment.status === 'active' && course.enrollment.progress_percentage > 0;
    if (statusFilter === 'not-started') return course.enrollment.progress_percentage === 0;
    return true;
  });

  const formatDuration = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    return `${hours}h`;
  };

  return (
    <div className="space-y-6">
      {/* User Info */}
      {user && user.employee_id && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500 text-white rounded-full">
                <Users className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-medium text-blue-900">Cursos Atribuídos</h3>
                <p className="text-sm text-blue-700">
                  Exibindo cursos atribuídos ao funcionário: <strong>{user.employee_name}</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* No Employee Linked Warning */}
      {user && !user.employee_id && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-500 text-white rounded-full">
                <AlertCircle className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-medium text-amber-900">Usuário Não Vinculado</h3>
                <p className="text-sm text-amber-700">
                  Este usuário não está vinculado a um funcionário. Contate o administrador para vincular a um funcionário e ter acesso aos cursos atribuídos.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between items-center">
            <div className="flex space-x-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Cursos</SelectItem>
                  <SelectItem value="in-progress">Em Progresso</SelectItem>
                  <SelectItem value="completed">Concluídos</SelectItem>
                  <SelectItem value="not-started">Não Iniciados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Badge variant="outline">
              {filteredCourses.length} curso(s)
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Courses List */}
      {filteredCourses.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhum curso encontrado</p>
            <p className="text-sm text-gray-400 mt-2">
              Explore o catálogo para se matricular em novos cursos
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredCourses.map(course => (
            <Card 
              key={course.id} 
              className="cursor-pointer hover:shadow-md transition-all"
              onClick={() => onCourseClick(course)}
            >
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-grow space-y-2 min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <BookOpen className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <h3 className="font-semibold text-lg">{course.title}</h3>

                      {course.enrollment.status === 'completed' && (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Concluído
                        </Badge>
                      )}

                      {course.is_mandatory && (
                        <Badge variant="destructive">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Obrigatório
                        </Badge>
                      )}
                    </div>

                    <p className="text-gray-600 text-sm">{course.short_description}</p>

                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{formatDuration(course.duration_hours)}</span>
                      </div>

                      {course.category && (
                        <Badge variant="outline">{course.category}</Badge>
                      )}

                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          Matrícula: {new Date(course.enrollment.enrolled_date).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Progresso</span>
                        <span>{course.enrollment.progress_percentage?.toFixed(1) || 0}%</span>
                      </div>
                      <Progress
                        value={course.enrollment.progress_percentage || 0}
                        className="h-2"
                      />
                    </div>
                  </div>

                  <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 sm:ml-6 flex-shrink-0">
                    <Button size="sm">
                      {course.enrollment.status === 'completed' ? (
                        <>
                          <Award className="h-4 w-4 mr-2" />
                          Revisar
                        </>
                      ) : (
                        <>
                          <PlayCircle className="h-4 w-4 mr-2" />
                          {course.enrollment.progress_percentage > 0 ? 'Continuar' : 'Iniciar'}
                        </>
                      )}
                    </Button>

                    {course.enrollment.certificate_issued && (
                      <Badge className="bg-purple-100 text-purple-800">
                        <Award className="h-3 w-3 mr-1" />
                        Certificado
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Certificates View Component
const CertificatesView = ({ certificates }) => {
  // API já está definida no escopo do módulo

  const handleViewCertificate = async (certificateId) => {
    try {
      const token = localStorage.getItem('token');
      
      // First, get a public access token for the certificate
      const tokenResponse = await axios.get(`${API}/api/certificates/${certificateId}/public-token`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (tokenResponse.data && tokenResponse.data.public_url) {
        // Open the public URL in a new tab
        window.open(tokenResponse.data.public_url, '_blank');
        toast.success('Certificado aberto em nova aba');
      } else {
        throw new Error('Não foi possível gerar link público para o certificado');
      }
    } catch (error) {
      console.error('Erro ao visualizar certificado:', error);
      toast.error('Erro ao visualizar certificado');
    }
  };

  const handleDownloadCertificate = async (certificateId, userName) => {
    try {
      const token = localStorage.getItem('token');
      
      // First, get a public access token for the certificate
      const tokenResponse = await axios.get(`${API}/api/certificates/${certificateId}/public-token`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (tokenResponse.data && tokenResponse.data.token) {
        // Use the public download endpoint
        const downloadUrl = `${API}/api/certificates/public/${tokenResponse.data.token}/download`;
        
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `certificado_${userName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Download do certificado iniciado!');
      } else {
        throw new Error('Não foi possível gerar link de download para o certificado');
      }
    } catch (error) {
      console.error('Erro ao baixar certificado:', error);
      toast.error('Erro ao baixar certificado');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Award className="h-5 w-5" />
            <span>Meus Certificados</span>
          </CardTitle>
          <CardDescription>
            Certificados obtidos pela conclusão de cursos
          </CardDescription>
        </CardHeader>
        <CardContent>
          {certificates.length === 0 ? (
            <div className="text-center py-12">
              <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum certificado obtido ainda</p>
              <p className="text-sm text-gray-400 mt-2">
                Complete cursos para obter certificados
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certificates.map(certificate => (
                <Card key={certificate.id} className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-orange-50">
                  <CardContent className="pt-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Award className="h-8 w-8 text-yellow-600" />
                        <Badge className="bg-yellow-100 text-yellow-800">
                          Válido
                        </Badge>
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-lg">
                          {certificate.certificate_data.course_title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {certificate.certificate_data.user_name}
                        </p>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Número:</span>
                          <span className="font-mono">{certificate.certificate_number}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Emissão:</span>
                          <span>{new Date(certificate.issued_date).toLocaleDateString('pt-BR')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Duração:</span>
                          <span>{certificate.certificate_data.course_duration}h</span>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => handleViewCertificate(certificate.id)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Visualizar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => handleDownloadCertificate(certificate.id, certificate.certificate_data.user_name)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Baixar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Course Admin Component
const EMPTY_NEW_COURSE = {
  title: '',
  description: '',
  short_description: '',
  category: '',
  level: 'beginner',
  duration_hours: 1,
  is_mandatory: false,
  tags: [],
  learning_objectives: [''],
  prerequisites: [''],
  competencies: [],
  passing_score: 70,
  quiz_max_attempts: 0,
  quiz_cooldown_minutes: 0,
  quiz_questions_to_draw: 0,
  quiz_shuffle_options: false,
  reaction_survey_enabled: false,
  validity_months: 0,
  auto_recycle: false,
  recycle_alert_days: 30,
  nr_code: '',
  lessons: [],
  is_external: false,
  external_url: '',
  external_certificate_url: '',
};

const EMPTY_LESSON = {
  title: '',
  description: '',
  content_type: 'video',
  content_url: '',
  content_data: {},
  duration_minutes: 0,
  order: 1,
  is_required: false,
  is_preview: false,
  quiz_questions: []
};

const CONTENT_TYPES_WITH_OPTIONAL_DURATION = new Set(['pdf', 'quiz']);

const lessonRequiresDuration = (contentType) => !CONTENT_TYPES_WITH_OPTIONAL_DURATION.has(contentType);

const getLessonDurationLabel = (contentType) =>
  lessonRequiresDuration(contentType) ? 'Duração (min) *' : 'Duração (min) opcional';

const CourseAdmin = ({ onCourseCreated, onAssignCourse, onEditCourseContent }) => {
  const [activeAdminTab, setActiveAdminTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [completionsCourse, setCompletionsCourse] = useState(null);
  const [completions, setCompletions] = useState([]);
  const [loadingCompletions, setLoadingCompletions] = useState(false);
  const [assignmentCourseId, setAssignmentCourseId] = useState('');
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [reportCourseFilter, setReportCourseFilter] = useState('all');
  const [reportStatusFilter, setReportStatusFilter] = useState('all');
  const [reportSearch, setReportSearch] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const allowedAdminTabs = ['courses', 'assignments', 'reports'];

  // Links públicos (convidados fazem o curso só com nome/e-mail)
  const [linksCourse, setLinksCourse] = useState(null);
  const [publicLinks, setPublicLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [newLink, setNewLink] = useState({ description: '', expires_at: '', max_uses: 0, employee_id: '' });
  const [linkEmpSearch, setLinkEmpSearch] = useState('');

  // E-mail dos funcionários (o código de acesso do link é enviado para ele)
  const [emailDrafts, setEmailDrafts] = useState({});
  const [emailSearch, setEmailSearch] = useState('');
  const saveEmployeeEmail = async (emp) => {
    const email = (emailDrafts[emp.id] || '').trim();
    if (!email) return;
    try {
      await axios.put(`${API}/api/lms/employees/${emp.id}/email`, { email });
      setEmployees((prev) => prev.map((x) => (x.id === emp.id ? { ...x, email } : x)));
      setEmailDrafts((d) => ({ ...d, [emp.id]: '' }));
      toast.success(`E-mail salvo no cadastro de ${emp.nome}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar e-mail');
    }
  };

  const loadPublicLinks = async (courseId) => {
    setLoadingLinks(true);
    try {
      const res = await axios.get(`${API}/api/lms/courses/${courseId}/public-links`);
      setPublicLinks(res.data || []);
    } catch (e) {
      toast.error('Erro ao carregar links públicos');
      setPublicLinks([]);
    } finally {
      setLoadingLinks(false);
    }
  };

  const openPublicLinks = (course) => {
    setLinksCourse(course);
    setLinkEmpSearch('');
    setNewLink({ description: '', expires_at: '', max_uses: 0, employee_id: '' });
    loadPublicLinks(course.id);
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado!');
    } catch (e) {
      window.prompt('Copie o link:', url);
    }
  };

  const createPublicLink = async () => {
    try {
      await axios.post(`${API}/api/lms/courses/${linksCourse.id}/public-links`, {
        description: newLink.description || null,
        expires_at: newLink.expires_at ? new Date(`${newLink.expires_at}T23:59:59`).toISOString() : null,
        max_uses: 0,
        employee_id: newLink.employee_id || null,
        identify_by: 'cpf',
      });
      toast.success('Link criado!');
      setNewLink({ description: '', expires_at: '', max_uses: 0, employee_id: '' });
      await loadPublicLinks(linksCourse.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar link');
    }
  };

  const deactivatePublicLink = async (linkId) => {
    if (!window.confirm('Desativar este link? Ninguém novo conseguirá iniciar o curso por ele (quem já começou continua).')) return;
    try {
      await axios.delete(`${API}/api/lms/public-links/${linkId}`);
      toast.success('Link desativado');
      await loadPublicLinks(linksCourse.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao desativar link');
    }
  };

  const handleViewCertificate = async (url) => {
    if (completionsCourse?.is_external) {
      // Certificado anexado pelo aluno: arquivo estático público, sem auth.
      window.open(resolveMediaUrl(url), '_blank');
      return;
    }
    // Certificado gerado pela plataforma: endpoint protegido, exige Bearer token.
    try {
      const token = localStorage.getItem('token');
      const pdfResponse = await axios.get(url, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const blobUrl = window.URL.createObjectURL(new Blob([pdfResponse.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (e) {
      toast.error('Não foi possível abrir o certificado');
    }
  };

  const handleViewCompletions = async (course) => {
    setCompletionsCourse(course);
    setLoadingCompletions(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/lms/courses/${course.id}/completions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletions(res.data || []);
    } catch (e) {
      toast.error('Erro ao carregar concluintes do curso');
      setCompletions([]);
    } finally {
      setLoadingCompletions(false);
    }
  };

  const loadCourseAssignments = async (courseId = assignmentCourseId) => {
    if (!courseId) return;
    setLoadingAssignments(true);
    try {
      const res = await axios.get(`${API}/api/lms/courses/${courseId}/assignments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCourseAssignments(res.data?.assignments || []);
    } catch (e) {
      toast.error('Erro ao carregar pessoas atribuídas');
      setCourseAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const params = new URLSearchParams();
      if (reportCourseFilter !== 'all') params.set('course_id', reportCourseFilter);
      if (reportStatusFilter !== 'all') params.set('status', reportStatusFilter);
      if (reportSearch.trim()) params.set('search', reportSearch.trim());
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API}/api/lms/reports/overview${query}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setReportData(res.data);
    } catch (e) {
      toast.error('Erro ao carregar relatórios');
      setReportData(null);
    } finally {
      setLoadingReports(false);
    }
  };
  
  // New course form
  const [newCourse, setNewCourse] = useState(EMPTY_NEW_COURSE);

  // Lesson management
  const [currentLesson, setCurrentLesson] = useState(EMPTY_LESSON);
  const [editingLessonIndex, setEditingLessonIndex] = useState(null);

  
  // Upload states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');


  // Helper functions
  const formatDuration = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    return `${hours}h`;
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: 'Iniciante',
      intermediate: 'Intermediário',
      advanced: 'Avançado'
    };
    return labels[level] || level;
  };


  // Upload file in chunks with progress
  const handleFileUpload = async (file, fileType) => {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
    const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB max
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      alert(`Arquivo muito grande! Tamanho máximo: 2GB. Tamanho do arquivo: ${(file.size / 1024 / 1024 / 1024).toFixed(2)}GB`);
      return null;
    }

    // Validate file type
    if (fileType === 'video') {
      const videoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov', 'video/quicktime'];
      if (!videoTypes.includes(file.type)) {
        alert('Tipo de vídeo inválido! Use MP4, WebM, OGG, AVI ou MOV');
        return null;
      }
    } else if (fileType === 'pdf') {
      if (file.type !== 'application/pdf') {
        alert('Tipo de arquivo inválido! Use apenas PDF');
        return null;
      }
    }

    try {
      setIsUploading(true);
      setUploadingFileName(file.name);
      setUploadProgress(0);

      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Upload each chunk
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('chunk_index', chunkIndex);
        formData.append('total_chunks', totalChunks);
        formData.append('upload_id', uploadId);
        formData.append('filename', file.name);
        formData.append('file_type', fileType);

        await axios.post(`${API}/api/lms/upload-chunk`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        // Update progress
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setUploadProgress(progress);
      }

      // Finalize upload
      const finalizeFormData = new FormData();
      finalizeFormData.append('upload_id', uploadId);
      finalizeFormData.append('filename', file.name);
      finalizeFormData.append('total_chunks', totalChunks);
      finalizeFormData.append('file_type', fileType);

      const response = await axios.post(`${API}/api/lms/finalize-upload`, finalizeFormData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      alert(`Upload concluído! ${file.name}`);
      
      setIsUploading(false);
      setUploadProgress(0);
      setUploadingFileName('');

      return response.data.url; // Return the file URL

    } catch (error) {
      console.error('Upload error:', error);
      alert(`Erro no upload: ${error.response?.data?.detail || error.message}`);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadingFileName('');
      return null;
    }
  };


  useEffect(() => {
    loadCourses();
    loadEmployees();
  }, []);

  useEffect(() => {
    if (!assignmentCourseId && courses.length > 0) {
      setAssignmentCourseId(courses[0].id);
    }
  }, [courses, assignmentCourseId]);

  useEffect(() => {
    if (activeAdminTab === 'assignments' && assignmentCourseId) {
      loadCourseAssignments(assignmentCourseId);
    }
  }, [activeAdminTab, assignmentCourseId]);

  useEffect(() => {
    if (activeAdminTab === 'reports') {
      loadReports();
    }
  }, [activeAdminTab, reportCourseFilter, reportStatusFilter]);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/api/lms/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/api/employees/lite`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const handleCancelCourseForm = () => {
    setNewCourse(EMPTY_NEW_COURSE);
    setCurrentLesson(EMPTY_LESSON);
    setEditingLessonIndex(null);
    setShowCreateCourse(false);
  };

  const handleEditCourse = async (course) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/lms/courses/${course.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const courseDetails = response.data || course;
      setNewCourse({
        id: courseDetails.id,
        title: courseDetails.title || '',
        description: courseDetails.description || '',
        short_description: courseDetails.short_description || '',
        category: courseDetails.category || '',
        level: courseDetails.level || 'beginner',
        duration_hours: courseDetails.duration_hours || 0,
        is_mandatory: !!courseDetails.is_mandatory,
        tags: courseDetails.tags || [],
        learning_objectives: courseDetails.learning_objectives?.length ? courseDetails.learning_objectives : [''],
        prerequisites: courseDetails.prerequisites?.length ? courseDetails.prerequisites : [''],
        competencies: courseDetails.competencies || [],
        passing_score: courseDetails.passing_score ?? 70,
        quiz_max_attempts: courseDetails.quiz_max_attempts ?? 0,
        quiz_cooldown_minutes: courseDetails.quiz_cooldown_minutes ?? 0,
        quiz_questions_to_draw: courseDetails.quiz_questions_to_draw ?? 0,
        quiz_shuffle_options: courseDetails.quiz_shuffle_options ?? false,
        reaction_survey_enabled: courseDetails.reaction_survey_enabled ?? false,
        validity_months: courseDetails.validity_months ?? 0,
        auto_recycle: courseDetails.auto_recycle ?? false,
        recycle_alert_days: courseDetails.recycle_alert_days ?? 30,
        nr_code: courseDetails.nr_code || '',
        lessons: courseDetails.lessons || [],
        is_external: !!courseDetails.is_external,
        external_url: courseDetails.external_url || '',
        external_certificate_url: courseDetails.external_certificate_url || '',
      });
      setShowCreateCourse(true);
    } catch (error) {
      console.error('Error loading course details:', error);
      toast.error('Erro ao carregar detalhes do curso');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCourseContent = (course) => {
    // Navigate to course content editor
    if (onEditCourseContent) {
      onEditCourseContent(course);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    // Confirmação antes de excluir
    if (!window.confirm('Tem certeza que deseja excluir este curso? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/lms/courses/${courseId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success('Curso excluído com sucesso!');
      
      // Recarregar lista de cursos
      loadCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao excluir curso';
      toast.error(errorMsg);
    }
  };

  const addLessonToCourse = () => {
    if (editingLessonIndex !== null) {
      // Atualizar aula existente
      setNewCourse(prev => ({
        ...prev,
        lessons: prev.lessons.map((lesson, index) => 
          index === editingLessonIndex ? {...currentLesson, id: lesson.id, order: lesson.order} : lesson
        )
      }));
      setEditingLessonIndex(null);
    } else {
      // Adicionar nova aula
      const newLesson = {
        ...currentLesson,
        id: Date.now().toString(),
        order: newCourse.lessons.length + 1
      };
      
      setNewCourse(prev => ({
        ...prev,
        lessons: [...prev.lessons, newLesson]
      }));
    }
    
    // Reset current lesson
    setCurrentLesson({
      title: '',
      description: '',
      content_type: 'video',
      content_url: '',
      content_data: {},
      duration_minutes: 0,
      is_required: false,
      is_preview: false,
      quiz_questions: []
    });
  };

  const removeLessonFromCourse = (lessonIndex) => {
    setNewCourse(prev => ({
      ...prev,
      lessons: prev.lessons.filter((_, index) => index !== lessonIndex)
    }));
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Clean up arrays
      const courseData = {
        ...newCourse,
        tags: newCourse.tags.filter(tag => tag.trim()),
        learning_objectives: newCourse.learning_objectives.filter(obj => obj.trim()),
        prerequisites: newCourse.prerequisites.filter(req => req.trim()),
        // Curso externo não tem aulas; interno não usa link/cert externos
        lessons: newCourse.is_external ? [] : newCourse.lessons,
      };

      let response;
      if (courseData.id) {
        // Update existing course
        response = await axios.put(`${API}/api/lms/courses/${courseData.id}`, courseData);
        toast.success('Curso atualizado com sucesso!');
      } else {
        // Create new course
        response = await axios.post(`${API}/api/lms/courses`, courseData);
        toast.success('Curso criado com sucesso!');
        
        // Create lessons for the new course
        const courseId = response.data.id;
        for (const lesson of courseData.lessons) {
          try {
            await axios.post(`${API}/api/lms/lessons`, {
              ...lesson,
              course_id: courseId
            });
          } catch (lessonError) {
            console.error('Error creating lesson:', lessonError);
          }
        }
      }
      
      // Reset form
      setNewCourse(EMPTY_NEW_COURSE);
      setCurrentLesson(EMPTY_LESSON);
      setEditingLessonIndex(null);

      setShowCreateCourse(false);
      loadCourses();
      if (onCourseCreated) onCourseCreated();
    } catch (error) {
      console.error('Error creating/updating course:', error);
      toast.error('Erro ao salvar curso');
    } finally {
      setLoading(false);
    }
  };

  const addArrayItem = (field) => {
    setNewCourse(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setNewCourse(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayItem = (field, index) => {
    setNewCourse(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Monitor className="h-5 w-5" />
            <span>Administração de Cursos</span>
          </CardTitle>
          <CardDescription>
            Gerencie cursos, aulas e atribuições do LMS
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeAdminTab} onValueChange={setActiveAdminTab}>
            <TabsList className="flex w-full items-center justify-start gap-1 overflow-x-auto sm:grid sm:grid-cols-3 sm:overflow-x-visible">
              {allowedAdminTabs.includes('courses') && <TabsTrigger value="courses" className="flex-shrink-0">Gerenciar Cursos</TabsTrigger>}
              {allowedAdminTabs.includes('assignments') && <TabsTrigger value="assignments" className="flex-shrink-0">Atribuições</TabsTrigger>}
              {allowedAdminTabs.includes('reports') && <TabsTrigger value="reports" className="flex-shrink-0">Relatórios</TabsTrigger>}
            </TabsList>

            {/* Courses Management Tab */}
            {allowedAdminTabs.includes('courses') && (
            <TabsContent value="courses" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Cursos Disponíveis</h3>
                <Button onClick={() => setShowCreateCourse(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Curso
                </Button>
              </div>

              {/* Course List */}
              <div className="space-y-4">
                {courses.map(course => (
                  <Card key={course.id} className="p-4">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                      <div className="space-y-2 min-w-0">
                        <h4 className="font-semibold">{course.title}</h4>
                        <p className="text-sm text-gray-600">{course.short_description}</p>
                        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm">
                          <Badge variant="outline">{course.category}</Badge>
                          <Badge className={LEVEL_COLORS[course.level]}>
                            {getLevelLabel(course.level)}
                          </Badge>
                          <span className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{formatDuration(course.duration_hours)}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Users className="h-3 w-3" />
                            <span>{course.enrollment_count || 0} matrículas</span>
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline"
                          onClick={() => handleEditCourse(course)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button size="sm" variant="secondary"
                          onClick={() => onAssignCourse(course)}
                        >
                          <UserCheck className="h-4 w-4 mr-2" />
                          Atribuir
                        </Button>
                        <Button size="sm" variant="outline"
                          onClick={() => handleViewCompletions(course)}
                        >
                          <Award className="h-4 w-4 mr-2" />
                          Concluintes
                        </Button>
                        {!course.is_external && (
                          <Button size="sm" variant="outline"
                            onClick={() => openPublicLinks(course)}
                          >
                            <Link className="h-4 w-4 mr-2" />
                            Link público
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteCourse(course.id)}
                          title="Excluir curso"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Create Course Modal */}
              {showCreateCourse && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle>{newCourse.id ? 'Editar Curso' : 'Criar Novo Curso'}</CardTitle>
                        <Button variant="outline" size="sm" onClick={handleCancelCourseForm}>
                          ×
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateCourse} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="title">Título *</Label>
                            <Input
                              id="title"
                              value={newCourse.title}
                              onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="category">Categoria</Label>
                            <Input
                              id="category"
                              value={newCourse.category}
                              onChange={(e) => setNewCourse({...newCourse, category: e.target.value})}
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="short_description">Descrição Curta</Label>
                          <Input
                            id="short_description"
                            value={newCourse.short_description}
                            onChange={(e) => setNewCourse({...newCourse, short_description: e.target.value})}
                          />
                        </div>

                        <div>
                          <Label htmlFor="description">Descrição Completa</Label>
                          <Textarea
                            id="description"
                            value={newCourse.description}
                            onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                            rows={3}
                          />
                        </div>

                        <div className={`grid ${newCourse.is_external ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
                          <div>
                            <Label htmlFor="level">Nível</Label>
                            <Select
                              value={newCourse.level}
                              onValueChange={(value) => setNewCourse({...newCourse, level: value})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="beginner">Iniciante</SelectItem>
                                <SelectItem value="intermediate">Intermediário</SelectItem>
                                <SelectItem value="advanced">Avançado</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="duration">Duração (horas)</Label>
                            <Input
                              id="duration"
                              type="number"
                              step="0.5"
                              min="0.5"
                              value={newCourse.duration_hours}
                              onChange={(e) => setNewCourse({...newCourse, duration_hours: parseFloat(e.target.value)})}
                            />
                          </div>
                          {!newCourse.is_external && (
                            <div className="flex items-end">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={newCourse.is_mandatory}
                                  onChange={(e) => setNewCourse({...newCourse, is_mandatory: e.target.checked})}
                                />
                                <span>Obrigatório</span>
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Avaliação avançada */}
                        <div className="border-t pt-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <HelpCircle className="h-4 w-4" /> Configurações de Avaliação
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label>Nota de aprovação (%)</Label>
                              <Input
                                type="number" min="0" max="100"
                                value={newCourse.passing_score}
                                onChange={(e) => setNewCourse({...newCourse, passing_score: parseInt(e.target.value) || 0})}
                              />
                            </div>
                            <div>
                              <Label>Tentativas máx. (0 = ilimitado)</Label>
                              <Input
                                type="number" min="0"
                                value={newCourse.quiz_max_attempts}
                                onChange={(e) => setNewCourse({...newCourse, quiz_max_attempts: parseInt(e.target.value) || 0})}
                              />
                            </div>
                            <div>
                              <Label>Intervalo entre tentativas (min)</Label>
                              <Input
                                type="number" min="0"
                                value={newCourse.quiz_cooldown_minutes}
                                onChange={(e) => setNewCourse({...newCourse, quiz_cooldown_minutes: parseInt(e.target.value) || 0})}
                              />
                            </div>
                            <div>
                              <Label>Sortear N questões (0 = todas)</Label>
                              <Input
                                type="number" min="0"
                                value={newCourse.quiz_questions_to_draw}
                                onChange={(e) => setNewCourse({...newCourse, quiz_questions_to_draw: parseInt(e.target.value) || 0})}
                              />
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={newCourse.quiz_shuffle_options}
                                  onChange={(e) => setNewCourse({...newCourse, quiz_shuffle_options: e.target.checked})}
                                />
                                <span>Embaralhar alternativas</span>
                              </label>
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={newCourse.reaction_survey_enabled}
                                  onChange={(e) => setNewCourse({...newCourse, reaction_survey_enabled: e.target.checked})}
                                />
                                <span>Pesquisa de reação ao concluir</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Conformidade / Validade */}
                        <div className="border-t pt-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Shield className="h-4 w-4" /> Conformidade & Validade
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label>Validade do certificado (meses, 0 = não expira)</Label>
                              <Input
                                type="number" min="0"
                                value={newCourse.validity_months}
                                onChange={(e) => setNewCourse({...newCourse, validity_months: parseInt(e.target.value) || 0})}
                              />
                            </div>
                            <div>
                              <Label>Alertar antes do vencimento (dias)</Label>
                              <Input
                                type="number" min="0"
                                value={newCourse.recycle_alert_days}
                                onChange={(e) => setNewCourse({...newCourse, recycle_alert_days: parseInt(e.target.value) || 0})}
                              />
                            </div>
                            <div>
                              <Label>NR associada (opcional)</Label>
                              <Input
                                value={newCourse.nr_code}
                                onChange={(e) => setNewCourse({...newCourse, nr_code: e.target.value})}
                                placeholder="Ex.: NR-35"
                              />
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={newCourse.auto_recycle}
                                  onChange={(e) => setNewCourse({...newCourse, auto_recycle: e.target.checked})}
                                />
                                <span>Reciclagem automática ao vencer</span>
                              </label>
                            </div>
                          </div>
                        </div>

                         {/* Tipo de Curso */}
                        <div className="border-t pt-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <BookOpen className="h-4 w-4" /> Tipo de Curso
                          </h4>
                          <div className="flex gap-6">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="course_source"
                                checked={!newCourse.is_external}
                                onChange={() => setNewCourse({...newCourse, is_external: false})}
                              />
                              <span>Interno (aulas no Grid)</span>
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="course_source"
                                checked={newCourse.is_external}
                                onChange={() => setNewCourse({...newCourse, is_external: true})}
                              />
                              <span>Externo (link + certificado)</span>
                            </label>
                          </div>
                        </div>

                        {/* Learning Objectives */}
                        <div>
                          <Label>Objetivos de Aprendizagem</Label>
                          {newCourse.learning_objectives.map((objective, index) => (
                            <div key={index} className="flex space-x-2 mt-2">
                              <Input
                                value={objective}
                                onChange={(e) => updateArrayItem('learning_objectives', index, e.target.value)}
                                placeholder="Digite um objetivo..."
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeArrayItem('learning_objectives', index)}
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => addArrayItem('learning_objectives')}
                            className="mt-2"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Adicionar Objetivo
                          </Button>
                        </div>

                        {/* Course Content Section — só para curso interno */}
                        {!newCourse.is_external && (
                        <div className="border-t pt-4">
                          <h4 className="text-lg font-semibold mb-4">Conteúdo do Curso</h4>
                          
                          {/* Lessons List */}
                          <div className="space-y-3 mb-4">
                            {newCourse.lessons.map((lesson, index) => (
                              <div key={index} className="border rounded-lg p-3 bg-gray-50">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-2">
                                      {lesson.content_type === 'video' && <Video className="h-4 w-4" />}
                                      {lesson.content_type === 'pdf' && <FileText className="h-4 w-4" />}
                                      {lesson.content_type === 'text' && <Type className="h-4 w-4" />}
                                      {lesson.content_type === 'quiz' && <HelpCircle className="h-4 w-4" />}
                                      <span className="font-medium">{lesson.title}</span>
                                      <Badge variant="outline">{lesson.content_type.toUpperCase()}</Badge>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-1">{lesson.description}</p>
                                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                                      <span>{lesson.duration_minutes} min</span>
                                      {lesson.content_url && (
                                        <span className="flex items-center space-x-1">
                                          <Link className="h-3 w-3" />
                                          <span>Link anexado</span>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        // Editar aula existente
                                        setCurrentLesson(lesson);
                                        setEditingLessonIndex(index);
                                      }}
                                      title="Editar aula"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeLessonFromCourse(index)}
                                      title="Excluir aula"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Add New Lesson */}
                          <div className="border rounded-lg p-4 space-y-4">
                            <h5 className="font-medium">Adicionar Nova Aula</h5>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="lesson_title">Título da Aula</Label>
                                <Input
                                  id="lesson_title"
                                  value={currentLesson.title}
                                  onChange={(e) => setCurrentLesson({...currentLesson, title: e.target.value})}
                                  placeholder="Nome da aula..."
                                />
                              </div>
                              <div>
                                <Label htmlFor="lesson_type">Tipo de Conteúdo</Label>
                                <Select 
                                  value={currentLesson.content_type} 
                                  onValueChange={(value) => setCurrentLesson({...currentLesson, content_type: value})}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="video">Vídeo</SelectItem>
                                    <SelectItem value="pdf">PDF</SelectItem>
                                    <SelectItem value="text">Texto</SelectItem>
                                    <SelectItem value="quiz">Quiz/Avaliação</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div>
                              <Label htmlFor="lesson_description">Descrição</Label>
                              <Textarea
                                id="lesson_description"
                                value={currentLesson.description}
                                onChange={(e) => setCurrentLesson({...currentLesson, description: e.target.value})}
                                placeholder="Descreva o conteúdo da aula..."
                                rows={2}
                              />
                            </div>

                            {/* Content URL for Video/PDF */}
                            {(currentLesson.content_type === 'video' || currentLesson.content_type === 'pdf') && (
                              <div>
                                <Label htmlFor="lesson_url">
                                  {currentLesson.content_type === 'video' ? 'URL do Vídeo (YouTube, Vimeo, etc.)' : 'URL do PDF'}
                                </Label>
                                <div className="flex space-x-2">
                                  <Input
                                    id="lesson_url"
                                    value={currentLesson.content_url}
                                    onChange={(e) => setCurrentLesson({...currentLesson, content_url: e.target.value})}
                                    placeholder={
                                      currentLesson.content_type === 'video' 
                                        ? "https://youtube.com/watch?v=..." 
                                        : "https://exemplo.com/arquivo.pdf"
                                    }
                                  />
                                  <input
                                    type="file"
                                    id="file-upload-lesson"
                                    accept={currentLesson.content_type === 'video' ? 'video/*' : '.pdf'}
                                    style={{ display: 'none' }}
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        const fileType = currentLesson.content_type === 'video' ? 'video' : 'pdf';
                                        const fileUrl = await handleFileUpload(file, fileType);
                                        if (fileUrl) {
                                          setCurrentLesson({...currentLesson, content_url: fileUrl});
                                        }
                                      }
                                      // Reset input
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => document.getElementById('file-upload-lesson')?.click()}
                                    disabled={isUploading}
                                  >
                                    <UploadIcon className="h-4 w-4 mr-2" />
                                    {isUploading ? 'Uploading...' : 'Upload'}
                                  </Button>
                                </div>
                                {isUploading && (
                                  <div className="mt-2 space-y-2">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-600">{uploadingFileName}</span>
                                      <span className="font-semibold">{uploadProgress}%</span>
                                    </div>
                                    <Progress value={uploadProgress} className="h-2" />
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Text Content */}
                            {currentLesson.content_type === 'text' && (
                              <div>
                                <Label htmlFor="lesson_text">Conteúdo de Texto</Label>
                                <Textarea
                                  id="lesson_text"
                                  value={currentLesson.content_data.text || ''}
                                  onChange={(e) => setCurrentLesson({
                                    ...currentLesson, 
                                    content_data: {...currentLesson.content_data, text: e.target.value}
                                  })}
                                  placeholder="Digite o conteúdo da aula..."
                                  rows={5}
                                />
                              </div>
                            )}

                            {/* Quiz Questions */}
                            {currentLesson.content_type === 'quiz' && (
                              <div>
                                <Label>Perguntas do Quiz</Label>
                                <div className="space-y-4">
                                  {(currentLesson.quiz_questions || []).map((question, qIndex) => (
                                    <div key={qIndex} className="border rounded-lg p-4 bg-gray-50">
                                      <div className="flex justify-between items-start mb-3">
                                        <Label className="font-medium">Pergunta {qIndex + 1}</Label>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            const updatedQuestions = (currentLesson.quiz_questions || []).filter((_, i) => i !== qIndex);
                                            setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      
                                      <div className="space-y-3">
                                        <Input
                                          placeholder="Digite a pergunta..."
                                          value={question.question_text}
                                          onChange={(e) => {
                                            const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                            updatedQuestions[qIndex] = {...question, question_text: e.target.value};
                                            setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                          }}
                                        />
                                        
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <Label>Tipo</Label>
                                            <Select
                                              value={question.question_type}
                                              onValueChange={(value) => {
                                                const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                updatedQuestions[qIndex] = {
                                                  ...question, 
                                                  question_type: value,
                                                  options: value === 'multiple_choice'
                                                    ? [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }]
                                                    : value === 'ordering' ? ['', '', '']
                                                    : [],
                                                  correct_data: value === 'ordering' ? { order: [] }
                                                    : value === 'matching' ? { pairs: [{ left: '', right: '' }, { left: '', right: '' }] }
                                                    : {}
                                                };
                                                setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                              }}
                                            >
                                              <SelectTrigger>
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="multiple_choice">Múltipla Escolha</SelectItem>
                                                <SelectItem value="true_false">Verdadeiro/Falso</SelectItem>
                                                <SelectItem value="short_answer">Resposta Curta</SelectItem>
                                                <SelectItem value="ordering">Ordenação</SelectItem>
                                                <SelectItem value="matching">Associação</SelectItem>
                                                <SelectItem value="essay">Dissertativa</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                          <div>
                                            <Label>Pontos</Label>
                                            <Input
                                              type="number"
                                              min="0"
                                              step="0.25"
                                              value={question.points}
                                              onChange={(e) => {
                                                const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                updatedQuestions[qIndex] = {...question, points: parseFloat(e.target.value)};
                                                setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                              }}
                                            />
                                          </div>
                                        </div>

                                        {/* Multiple Choice Options */}
                                        {question.question_type === 'multiple_choice' && (
                                          <div>
                                            <Label>Opções de Resposta</Label>
                                            {(question.options || [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }]).map((option, optIndex) => (
                                              <div key={optIndex} className="flex space-x-2 mt-2">
                                                <Input
                                                  placeholder={`Opção ${optIndex + 1}...`}
                                                  value={option.text || ''}
                                                  onChange={(e) => {
                                                    const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                    const updatedOptions = [...(question.options || [])];
                                                    updatedOptions[optIndex] = { ...option, text: e.target.value };
                                                    updatedQuestions[qIndex] = {...question, options: updatedOptions};
                                                    setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                                  }}
                                                />
                                                <input
                                                  type="radio"
                                                  name={`correct_${qIndex}`}
                                                  checked={question.correct_answer === option.text}
                                                  onChange={() => {
                                                    const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                    const updatedOptions = (question.options || []).map((o) => ({ ...o, is_correct: o.text === option.text }));
                                                    updatedQuestions[qIndex] = {...question, options: updatedOptions, correct_answer: option.text};
                                                    setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                                  }}
                                                  title="Resposta correta"
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* True/False */}
                                        {question.question_type === 'true_false' && (
                                          <div>
                                            <Label>Resposta Correta</Label>
                                            <Select
                                              value={question.correct_answer}
                                              onValueChange={(value) => {
                                                const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                updatedQuestions[qIndex] = {...question, correct_answer: value};
                                                setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                              }}
                                            >
                                              <SelectTrigger>
                                                <SelectValue placeholder="Selecione..." />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="true">Verdadeiro</SelectItem>
                                                <SelectItem value="false">Falso</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}

                                        {/* Short Answer */}
                                        {question.question_type === 'short_answer' && (
                                          <div>
                                            <Label>Resposta Correta</Label>
                                            <Input
                                              placeholder="Digite a resposta correta..."
                                              value={question.correct_answer}
                                              onChange={(e) => {
                                                const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                updatedQuestions[qIndex] = {...question, correct_answer: e.target.value};
                                                setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                              }}
                                            />
                                          </div>
                                        )}

                                        {/* Ordering — itens na ordem correta */}
                                        {question.question_type === 'ordering' && (
                                          <div>
                                            <Label>Itens na ordem correta</Label>
                                            <p className="text-xs text-gray-500 mb-2">Digite os itens na sequência correta. O aluno verá embaralhado.</p>
                                            {((question.correct_data && question.correct_data.order) || []).map((item, oi) => (
                                              <div key={oi} className="flex space-x-2 mt-2">
                                                <span className="self-center text-sm text-gray-500">{oi + 1}.</span>
                                                <Input
                                                  placeholder={`Item ${oi + 1}...`}
                                                  value={item}
                                                  onChange={(e) => {
                                                    const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                    const order = [...((question.correct_data && question.correct_data.order) || [])];
                                                    order[oi] = e.target.value;
                                                    updatedQuestions[qIndex] = {...question, correct_data: {...(question.correct_data || {}), order}};
                                                    setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                                  }}
                                                />
                                                <Button type="button" variant="outline" size="sm" onClick={() => {
                                                  const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                  const order = ((question.correct_data && question.correct_data.order) || []).filter((_, i) => i !== oi);
                                                  updatedQuestions[qIndex] = {...question, correct_data: {...(question.correct_data || {}), order}};
                                                  setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                                }}>×</Button>
                                              </div>
                                            ))}
                                            <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => {
                                              const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                              const order = [...((question.correct_data && question.correct_data.order) || []), ''];
                                              updatedQuestions[qIndex] = {...question, correct_data: {...(question.correct_data || {}), order}};
                                              setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                            }}>
                                              <Plus className="h-4 w-4 mr-2" /> Adicionar item
                                            </Button>
                                          </div>
                                        )}

                                        {/* Matching — pares esquerda/direita */}
                                        {question.question_type === 'matching' && (
                                          <div>
                                            <Label>Pares de associação</Label>
                                            <p className="text-xs text-gray-500 mb-2">Coluna A ↔ Coluna B (correspondência correta).</p>
                                            {((question.correct_data && question.correct_data.pairs) || []).map((pair, pi) => (
                                              <div key={pi} className="flex space-x-2 mt-2">
                                                <Input
                                                  placeholder="Coluna A"
                                                  value={pair.left}
                                                  onChange={(e) => {
                                                    const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                    const pairs = [...((question.correct_data && question.correct_data.pairs) || [])];
                                                    pairs[pi] = {...pairs[pi], left: e.target.value};
                                                    updatedQuestions[qIndex] = {...question, correct_data: {...(question.correct_data || {}), pairs}};
                                                    setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                                  }}
                                                />
                                                <Input
                                                  placeholder="Coluna B"
                                                  value={pair.right}
                                                  onChange={(e) => {
                                                    const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                    const pairs = [...((question.correct_data && question.correct_data.pairs) || [])];
                                                    pairs[pi] = {...pairs[pi], right: e.target.value};
                                                    updatedQuestions[qIndex] = {...question, correct_data: {...(question.correct_data || {}), pairs}};
                                                    setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                                  }}
                                                />
                                                <Button type="button" variant="outline" size="sm" onClick={() => {
                                                  const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                                  const pairs = ((question.correct_data && question.correct_data.pairs) || []).filter((_, i) => i !== pi);
                                                  updatedQuestions[qIndex] = {...question, correct_data: {...(question.correct_data || {}), pairs}};
                                                  setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                                }}>×</Button>
                                              </div>
                                            ))}
                                            <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => {
                                              const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                              const pairs = [...((question.correct_data && question.correct_data.pairs) || []), { left: '', right: '' }];
                                              updatedQuestions[qIndex] = {...question, correct_data: {...(question.correct_data || {}), pairs}};
                                              setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                            }}>
                                              <Plus className="h-4 w-4 mr-2" /> Adicionar par
                                            </Button>
                                          </div>
                                        )}

                                        {/* Essay — correção manual */}
                                        {question.question_type === 'essay' && (
                                          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800">
                                            Questão dissertativa: a resposta do aluno ficará pendente de correção manual (não aprova automaticamente).
                                          </div>
                                        )}

                                        <div>
                                          <Label>Explicação (opcional)</Label>
                                          <Textarea
                                            placeholder="Explicação da resposta..."
                                            value={question.explanation}
                                            onChange={(e) => {
                                              const updatedQuestions = [...(currentLesson.quiz_questions || [])];
                                              updatedQuestions[qIndex] = {...question, explanation: e.target.value};
                                              setCurrentLesson({...currentLesson, quiz_questions: updatedQuestions});
                                            }}
                                            rows={2}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                      const newQuestion = {
                                        question_text: '',
                                        question_type: 'multiple_choice',
                                        options: [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }],
                                        correct_answer: '',
                                        explanation: '',
                                        points: 1,
                                        order: (currentLesson.quiz_questions || []).length + 1
                                      };
                                      setCurrentLesson({
                                        ...currentLesson,
                                        quiz_questions: [...(currentLesson.quiz_questions || []), newQuestion]
                                      });
                                    }}
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Adicionar Pergunta
                                  </Button>
                                </div>
                              </div>
                            )}

                            <div className="flex justify-between items-center">
                              <div className="flex items-center space-x-4">
                                <div>
                                  <Label htmlFor="lesson_duration">{getLessonDurationLabel(currentLesson.content_type)}</Label>
                                  <Input
                                    id="lesson_duration"
                                    type="number"
                                    min="0"
                                    value={currentLesson.duration_minutes || ''}
                                    onChange={(e) => setCurrentLesson({...currentLesson, duration_minutes: parseInt(e.target.value) || 0})}
                                    className="w-20"
                                  />
                                </div>
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={currentLesson.is_required}
                                    onChange={(e) => setCurrentLesson({...currentLesson, is_required: e.target.checked})}
                                  />
                                  <Label>Obrigatória</Label>
                                </div>
                              </div>
                              <Button
                                type="button"
                                onClick={addLessonToCourse}
                                disabled={!currentLesson.title.trim() || (lessonRequiresDuration(currentLesson.content_type) && !currentLesson.duration_minutes)}
                              >
                                {editingLessonIndex !== null ? (
                                  <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Atualizar Aula
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Adicionar Aula
                                  </>
                                )}
                              </Button>
                              {editingLessonIndex !== null && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingLessonIndex(null);
                                    setCurrentLesson(EMPTY_LESSON);
                                  }}
                                >
                                  Cancelar Edição
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        )}

                        {/* Curso Externo */}
                        {newCourse.is_external && (
                          <div className="border-t pt-4 space-y-4">
                            <h4 className="text-lg font-semibold">Curso Externo</h4>
                            <div>
                              <Label htmlFor="external_url">Link do Curso</Label>
                              <Input
                                id="external_url"
                                value={newCourse.external_url}
                                onChange={(e) => setNewCourse({...newCourse, external_url: e.target.value})}
                                placeholder="https://..."
                              />
                            </div>
                          </div>
                        )}
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button type="button" variant="outline" onClick={handleCancelCourseForm}>
                            Cancelar
                          </Button>
                          <Button type="submit" disabled={loading}>
                            {loading ? 'Salvando...' : (newCourse.id ? 'Salvar' : 'Criar Curso')}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Completions Modal */}
              {completionsCourse && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>Concluintes</CardTitle>
                          <CardDescription>{completionsCourse.title}</CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setCompletionsCourse(null)}>
                          ×
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loadingCompletions ? (
                        <p className="text-sm text-gray-500 text-center py-8">Carregando...</p>
                      ) : completions.length === 0 ? (
                        <div className="text-center py-8">
                          <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">Ninguém concluiu este curso ainda</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {completions.map((c) => (
                            <div key={c.enrollment_id} className="flex items-center justify-between border rounded-lg p-3">
                              <div>
                                <p className="font-medium">
                                  {c.user_name}
                                  {c.is_guest && <Badge variant="secondary" className="ml-2 text-xs">Convidado</Badge>}
                                </p>
                                {c.guest_email && <p className="text-xs text-gray-500">{c.guest_email}</p>}
                                <p className="text-xs text-gray-500">
                                  Concluído em {c.completed_at ? new Date(c.completed_at).toLocaleDateString('pt-BR') : '—'}
                                  {c.certificate_number ? ` · ${c.certificate_number}` : ''}
                                </p>
                              </div>
                              {c.certificate_url ? (
                                <Button size="sm" variant="outline"
                                  onClick={() => handleViewCertificate(c.certificate_url)}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  Ver certificado
                                </Button>
                              ) : (
                                <span className="text-xs text-gray-400">Sem certificado</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
              {/* Public Links Modal */}
              {linksCourse && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>Link público do curso</CardTitle>
                          <CardDescription>
                            {linksCourse.title} — um único link para todos: cada funcionário informa o CPF, faz o curso sem precisar de usuário, o certificado sai no nome do cadastro e o curso fica registrado no cadastro dele.
                          </CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setLinksCourse(null)}>
                          ×
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="border rounded-lg p-4 space-y-3">
                        <h4 className="font-semibold text-sm">Gerar novo link</h4>
                        <div>
                          <Label htmlFor="link_emp">Para quem é o link</Label>
                          <Input
                            placeholder="Buscar funcionário pelo nome..."
                            value={linkEmpSearch}
                            onChange={(e) => setLinkEmpSearch(e.target.value)}
                            className="mb-2"
                          />
                          <select
                            id="link_emp"
                            className="w-full border rounded-md h-9 px-2 text-sm bg-white"
                            value={newLink.employee_id}
                            onChange={(e) => setNewLink({ ...newLink, employee_id: e.target.value })}
                          >
                            <option value="">Link único — cada funcionário se identifica pelo CPF</option>
                            {employees
                              .filter((emp) => !linkEmpSearch || (emp.nome || '').toLowerCase().includes(linkEmpSearch.toLowerCase()))
                              .slice(0, 200)
                              .map((emp) => (
                                <option key={emp.id} value={emp.id}>{emp.nome}{emp.cargo ? ` — ${emp.cargo}` : ''}</option>
                              ))}
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="link_desc">Descrição (opcional)</Label>
                          <Input
                            id="link_desc"
                            value={newLink.description}
                            onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                            placeholder="Ex.: Turma de outubro - terceirizados"
                          />
                        </div>
                        <div>
                          <Label htmlFor="link_exp">Válido até (opcional — sem data, o link não expira)</Label>
                          <Input
                            id="link_exp"
                            type="date"
                            value={newLink.expires_at}
                            onChange={(e) => setNewLink({ ...newLink, expires_at: e.target.value })}
                          />
                        </div>
                        <div className="flex justify-end">
                          <Button size="sm" onClick={createPublicLink}>
                            <Plus className="h-4 w-4 mr-2" />
                            Gerar link
                          </Button>
                        </div>
                      </div>

                      {/* Funcionários sem e-mail: não conseguem receber o código de acesso */}
                      {(() => {
                        const semEmail = employees.filter((x) => !(x.email || '').trim());
                        if (semEmail.length === 0) return null;
                        const visiveis = semEmail.filter((x) => !emailSearch || (x.nome || '').toLowerCase().includes(emailSearch.toLowerCase()));
                        return (
                          <div className="border rounded-lg p-4 space-y-3 bg-amber-50 border-amber-200">
                            <div>
                              <h4 className="font-semibold text-sm">Funcionários sem e-mail ({semEmail.length})</h4>
                              <p className="text-xs text-gray-600">
                                O link envia um código de acesso para o e-mail do funcionário. Quem não tem e-mail não consegue entrar: cadastre aqui e ele é salvo no cadastro do funcionário.
                              </p>
                            </div>
                            <Input
                              placeholder="Buscar funcionário sem e-mail..."
                              value={emailSearch}
                              onChange={(e) => setEmailSearch(e.target.value)}
                            />
                            <div className="max-h-56 overflow-y-auto space-y-2">
                              {visiveis.map((emp) => (
                                <div key={emp.id} className="flex items-center gap-2">
                                  <span className="text-sm flex-1 min-w-0 truncate">{emp.nome}</span>
                                  <Input
                                    type="email"
                                    className="w-56 text-sm"
                                    placeholder="email@empresa.com"
                                    value={emailDrafts[emp.id] || ''}
                                    onChange={(e) => setEmailDrafts({ ...emailDrafts, [emp.id]: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveEmployeeEmail(emp); }}
                                  />
                                  <Button size="sm" variant="outline" disabled={!(emailDrafts[emp.id] || '').trim()} onClick={() => saveEmployeeEmail(emp)}>
                                    Salvar
                                  </Button>
                                </div>
                              ))}
                              {visiveis.length === 0 && <p className="text-xs text-gray-500">Nenhum resultado.</p>}
                            </div>
                          </div>
                        );
                      })()}

                      {loadingLinks ? (
                        <p className="text-sm text-gray-500 text-center py-4">Carregando...</p>
                      ) : publicLinks.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">Nenhum link criado para este curso</p>
                      ) : (
                        <div className="space-y-2">
                          {publicLinks.map((l) => (
                            <div key={l.id} className="border rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-sm">
                                  <span className="font-medium">{l.employee_name || l.description || (l.identify_by === 'cpf' ? 'Link único (CPF)' : 'Link aberto')}</span>
                                  {l.employee_name && <Badge variant="outline" className="ml-2 text-xs">Funcionário</Badge>}
                                  <span className="text-xs text-gray-500 ml-2">
                                    {l.current_uses || 0}{l.max_uses > 0 ? `/${l.max_uses}` : ''} participante(s)
                                    {l.expires_at ? ` · até ${new Date(l.expires_at).toLocaleDateString('pt-BR')}` : ''}
                                  </span>
                                </div>
                                {l.is_active ? (
                                  <Badge className="bg-green-100 text-green-800">Ativo</Badge>
                                ) : (
                                  <Badge variant="secondary">Desativado</Badge>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Input readOnly value={l.public_url} onFocus={(e) => e.target.select()} className="text-xs" />
                                <Button size="sm" variant="outline" onClick={() => copyLink(l.public_url)}>
                                  Copiar
                                </Button>
                                {l.is_active && (
                                  <Button size="sm" variant="destructive" onClick={() => deactivatePublicLink(l.id)}>
                                    Desativar
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
            )}

            {/* Assignments Tab */}
            {allowedAdminTabs.includes('assignments') && (
            <TabsContent value="assignments" className="space-y-4">
              <div className="space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold">Atribuições do Curso</h3>
                    <p className="text-sm text-gray-500">Veja quem está matriculado e acompanhe o progresso por pessoa.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={assignmentCourseId} onValueChange={setAssignmentCourseId}>
                      <SelectTrigger className="w-full sm:w-72">
                        <SelectValue placeholder="Selecione um curso" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map(course => (
                          <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      onClick={() => loadCourseAssignments()}
                      disabled={!assignmentCourseId || loadingAssignments}
                    >
                      <Search className="h-4 w-4 mr-2" />
                      Atualizar
                    </Button>
                    <Button
                      onClick={() => {
                        const course = courses.find(c => c.id === assignmentCourseId);
                        if (course) onAssignCourse(course);
                      }}
                      disabled={!assignmentCourseId}
                    >
                      <UserCheck className="h-4 w-4 mr-2" />
                      Atribuir
                    </Button>
                  </div>
                </div>

                <Card>
                  <CardContent className="pt-4">
                    {loadingAssignments ? (
                      <p className="text-sm text-gray-500 text-center py-8">Carregando atribuições...</p>
                    ) : courseAssignments.length === 0 ? (
                      <div className="text-center py-8">
                        <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">Nenhuma pessoa atribuída a este curso</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {courseAssignments.map((assignment) => (
                          <div key={assignment.enrollment_id} className="border rounded-lg p-3">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-medium">{assignment.user?.name}</p>
                                <p className="text-sm text-gray-500">
                                  {assignment.user?.email || 'Sem e-mail'}
                                  {assignment.user?.role ? ` · ${assignment.user.role}` : ''}
                                </p>
                                <p className="text-xs text-gray-400">
                                  Matrícula: {assignment.enrolled_date ? new Date(assignment.enrolled_date).toLocaleDateString('pt-BR') : '—'}
                                </p>
                              </div>
                              <div className="w-full lg:w-64 space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <Badge variant={assignment.status === 'completed' ? 'default' : 'secondary'}>
                                    {assignment.status === 'completed' ? 'Concluído' : assignment.progress_percentage > 0 ? 'Em progresso' : 'Não iniciado'}
                                  </Badge>
                                  <span>{Number(assignment.progress_percentage || 0).toFixed(1)}%</span>
                                </div>
                                <Progress value={assignment.progress_percentage || 0} className="h-2" />
                                <p className="text-xs text-gray-500">
                                  {assignment.completed_lessons || 0}/{assignment.total_lessons || 0} aulas
                                  {assignment.certificate_issued ? ' · certificado emitido' : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            )}

            {/* Reports Tab */}
            {allowedAdminTabs.includes('reports') && (
            <TabsContent value="reports" className="space-y-4">
              <div className="space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold">Relatórios de Aprendizagem</h3>
                    <p className="text-sm text-gray-500">Resumo operacional de matrículas, progresso, certificados e quizzes.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={reportCourseFilter} onValueChange={setReportCourseFilter}>
                      <SelectTrigger className="w-full sm:w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os cursos</SelectItem>
                        {courses.map(course => (
                          <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={reportStatusFilter} onValueChange={setReportStatusFilter}>
                      <SelectTrigger className="w-full sm:w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os status</SelectItem>
                        <SelectItem value="active">Ativos</SelectItem>
                        <SelectItem value="completed">Concluídos</SelectItem>
                        <SelectItem value="dropped">Cancelados</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Input
                        value={reportSearch}
                        onChange={(e) => setReportSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && loadReports()}
                        placeholder="Buscar pessoa ou curso"
                        className="w-full sm:w-56"
                      />
                      <Button variant="outline" onClick={loadReports} disabled={loadingReports}>
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Matrículas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{reportData?.summary?.total_enrollments || 0}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Concluídos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">{reportData?.summary?.completed || 0}</div>
                      <p className="text-xs text-gray-500">{reportData?.summary?.completion_rate || 0}% de conclusão</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Em progresso</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-orange-600">{reportData?.summary?.in_progress || 0}</div>
                      <p className="text-xs text-gray-500">{reportData?.summary?.not_started || 0} não iniciados</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Certificados / Quizzes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-blue-600">{reportData?.summary?.certificates_issued || 0}</div>
                      <p className="text-xs text-gray-500">{reportData?.summary?.quiz_attempts || 0} tentativas de quiz</p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardContent className="pt-4">
                    {loadingReports ? (
                      <p className="text-sm text-gray-500 text-center py-8">Carregando relatório...</p>
                    ) : !reportData?.rows?.length ? (
                      <div className="text-center py-8">
                        <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">Nenhuma matrícula encontrada para os filtros atuais</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-gray-500">
                              <th className="py-2 pr-4 font-medium">Curso</th>
                              <th className="py-2 pr-4 font-medium">Pessoa</th>
                              <th className="py-2 pr-4 font-medium">Status</th>
                              <th className="py-2 pr-4 font-medium">Progresso</th>
                              <th className="py-2 pr-4 font-medium">Certificado</th>
                              <th className="py-2 font-medium">Quiz</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.rows.map(row => (
                              <tr key={row.enrollment_id} className="border-b">
                                <td className="py-3 pr-4">
                                  <p className="font-medium">{row.course_title}</p>
                                  <p className="text-xs text-gray-500">{row.course_category || 'Sem categoria'}</p>
                                </td>
                                <td className="py-3 pr-4">
                                  <p className="font-medium">{row.user?.name}</p>
                                  <p className="text-xs text-gray-500">{row.user?.email || 'Sem e-mail'}</p>
                                </td>
                                <td className="py-3 pr-4">
                                  <Badge variant={row.status === 'completed' ? 'default' : 'secondary'}>
                                    {row.status === 'completed' ? 'Concluído' : row.progress_percentage > 0 ? 'Em progresso' : 'Não iniciado'}
                                  </Badge>
                                </td>
                                <td className="py-3 pr-4 min-w-40">
                                  <div className="flex items-center gap-2">
                                    <Progress value={row.progress_percentage || 0} className="h-2" />
                                    <span className="text-xs">{Number(row.progress_percentage || 0).toFixed(0)}%</span>
                                  </div>
                                </td>
                                <td className="py-3 pr-4">{row.certificate_issued ? 'Emitido' : '—'}</td>
                                <td className="py-3">{row.quiz_attempts || 0} tentativa(s)</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

// Course Detail Modal Component
const CourseDetailModal = ({ course, onClose, onEnroll, onReload, onAccessCourse }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const formatDuration = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    return `${hours}h`;
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: 'Iniciante',
      intermediate: 'Intermediário',
      advanced: 'Avançado'
    };
    return labels[level] || level;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <CardTitle className="text-2xl">{course.title}</CardTitle>
              <div className="flex items-center space-x-4">
                {course.category && (
                  <Badge variant="outline">{course.category}</Badge>
                )}
                <Badge className={LEVEL_COLORS[course.level]}>
                  {getLevelLabel(course.level)}
                </Badge>
                <div className="flex items-center space-x-1 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>{formatDuration(course.duration_hours)}</span>
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={onClose}>
              ×
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="curriculum">Conteúdo</TabsTrigger>
              <TabsTrigger value="details">Detalhes</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Descrição</h3>
                <p className="text-gray-600">{course.description}</p>
              </div>
              
              {course.learning_objectives && course.learning_objectives.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Objetivos de Aprendizagem</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {course.learning_objectives.map((objective, index) => (
                      <li key={index}>{objective}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {course.instructor && (
                <div>
                  <h3 className="font-semibold mb-2">Instrutor</h3>
                  <div className="flex items-center space-x-2">
                    <UserCheck className="h-4 w-4 text-blue-600" />
                    <span>{course.instructor.nome}</span>
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="curriculum" className="space-y-4">
              {course.lessons && course.lessons.length > 0 ? (
                <div className="space-y-3">
                  {course.lessons.map((lesson, index) => {
                    const ContentIcon = CONTENT_ICONS[lesson.content_type] || FileText;
                    return (
                      <div key={lesson.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                        <ContentIcon className="h-5 w-5 text-blue-600" />
                        <div className="flex-grow">
                          <h4 className="font-medium">{lesson.title}</h4>
                          {lesson.description && (
                            <p className="text-sm text-gray-600">{lesson.description}</p>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {lesson.duration_minutes}min
                        </div>
                        {lesson.is_preview && (
                          <Badge variant="secondary">Preview</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Conteúdo em preparação</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Informações</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Duração:</span>
                      <span>{formatDuration(course.duration_hours)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Nível:</span>
                      <span>{getLevelLabel(course.level)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Matrículas:</span>
                      <span>{course.enrollment_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Idioma:</span>
                      <span>Português (BR)</span>
                    </div>
                  </div>
                </div>
                
                {course.tags && course.tags.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-1">
                      {course.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {course.prerequisites && course.prerequisites.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Pré-requisitos</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {course.prerequisites.map((prereq, index) => (
                      <li key={index}>{prereq}</li>
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end space-x-2 pt-6 border-t">
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
            {(!course.is_enrolled && !course.enrollment) ? (
              <Button onClick={() => onEnroll(course.id)}>
                <BookmarkPlus className="h-4 w-4 mr-2" />
                Matricular-se
              </Button>
            ) : (
              <Button onClick={() => onAccessCourse(course.id)}>
                <PlayCircle className="h-4 w-4 mr-2" />
                Acessar Curso
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Normaliza a URL de mídia para o caminho relativo /api/static/... (funciona em
// dev e produção, atrás do proxy). Mantém YouTube e links externos intactos.
const resolveMediaUrl = (url) => {
  if (!url) return url;
  const apiIdx = url.indexOf('/api/static/');
  if (apiIdx >= 0) return url.slice(apiIdx);
  const sIdx = url.indexOf('/static/');
  if (sIdx >= 0) return '/api' + url.slice(sIdx);
  return url;
};

// Player de vídeo controlado: registra o tempo realmente assistido e bloqueia o
// avanço (seek) além do ponto já assistido — evita "acelerar" o curso.
const ControlledVideo = ({ src, onProgress }) => {
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(0);

  const report = (v) => {
    const dur = v.duration || 0;
    const pct = dur > 0 ? Math.min(100, (maxWatchedRef.current / dur) * 100) : 0;
    onProgress && onProgress({
      watchedSeconds: maxWatchedRef.current,
      durationSeconds: dur,
      watchedPct: pct,
    });
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    // Só avança o ponto máximo em reprodução natural (tolerância de 1s)
    if (v.currentTime <= maxWatchedRef.current + 1 && v.currentTime > maxWatchedRef.current) {
      maxWatchedRef.current = v.currentTime;
    }
    report(v);
  };

  const handleSeeking = () => {
    const v = videoRef.current;
    if (!v) return;
    // Bloqueia pular para frente além do já assistido (permite rever para trás)
    if (v.currentTime > maxWatchedRef.current + 1.5) {
      v.currentTime = maxWatchedRef.current;
    }
  };

  return (
    <video
      ref={videoRef}
      controls
      controlsList="nodownload noplaybackrate"
      disablePictureInPicture
      onContextMenu={(e) => e.preventDefault()}
      className="w-full h-full rounded-lg"
      onLoadedMetadata={() => videoRef.current && report(videoRef.current)}
      onTimeUpdate={handleTimeUpdate}
      onSeeking={handleSeeking}
    >
      <source src={src} />
      Seu navegador não suporta o elemento de vídeo.
    </video>
  );
};

// Player de vídeos hospedados no Bunny Stream. Busca no backend uma URL de
// reprodução assinada e de curta duração (MP4 com token de CDN com escopo de
// diretório), exibe "Processando" enquanto o Bunny transcodifica e reproduz num
// <video> nativo — o que garante reprodução em qualquer navegador e controle
// total do anti-skip. Caso o MP4 não esteja disponível, recorre ao player
// embutido (iframe) como último recurso.
const BunnyVideo = ({ videoId, onProgress, guestSession }) => {
  // Convidado (link público) usa a rota por sessão, sem autenticação
  const playUrl = guestSession
    ? `${API}/api/lms-guest/${guestSession}/video/${videoId}/play`
    : `${API}/api/lms/video/${videoId}/play`;
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const retriedRef = useRef(false);
  const [state, setState] = useState('loading'); // loading | processing | ready | error
  const [progress, setProgress] = useState(0);
  const [playData, setPlayData] = useState(null);

  const report = (v) => {
    const dur = (v && v.duration) || 0;
    const pct = dur > 0 ? Math.min(100, (maxWatchedRef.current / dur) * 100) : 0;
    onProgress && onProgress({
      watchedSeconds: maxWatchedRef.current,
      durationSeconds: dur,
      watchedPct: pct,
    });
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    // Só avança o ponto máximo em reprodução natural (tolerância de 1s)
    if (v.currentTime <= maxWatchedRef.current + 1 && v.currentTime > maxWatchedRef.current) {
      maxWatchedRef.current = v.currentTime;
    }
    report(v);
  };

  const handleSeeking = () => {
    const v = videoRef.current;
    if (!v) return;
    // Bloqueia pular para frente além do já assistido (permite rever para trás)
    if (v.currentTime > maxWatchedRef.current + 1.5) {
      v.currentTime = maxWatchedRef.current;
    }
  };

  // Se o token da URL expirar durante a reprodução (range request → 403), o
  // <video> dispara erro. Reconsulta o /play uma vez para obter uma URL nova e
  // retoma de onde parou, preservando o anti-skip (maxWatchedRef).
  const handleVideoError = async () => {
    if (retriedRef.current) return;
    retriedRef.current = true;
    const v = videoRef.current;
    const resume = v ? v.currentTime : 0;
    try {
      const resp = await axios.get(playUrl, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = resp.data || {};
      if (data.status === 'ready' && (data.mp4_url || data.embed_url)) {
        setPlayData(data);
        setTimeout(() => {
          const nv = videoRef.current;
          if (nv && data.mp4_url) {
            try { nv.load(); nv.currentTime = resume; nv.play().catch(() => {}); } catch (e) { /* noop */ }
          }
        }, 0);
      }
    } catch (e) { /* noop */ }
  };

  // Busca o estado/URL de reprodução; reconsulta enquanto estiver "processing".
  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    const fetchPlay = async () => {
      try {
        const resp = await axios.get(playUrl, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (cancelled) return;
        const data = resp.data || {};
        if (data.status === 'ready') {
          setPlayData(data);
          setState('ready');
        } else if (data.status === 'processing') {
          setProgress(data.progress || 0);
          setState('processing');
          pollTimer = setTimeout(fetchPlay, 10000);
        } else {
          setState('error');
        }
      } catch (e) {
        if (!cancelled) setState('error');
      }
    };

    fetchPlay();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [videoId]);

  if (state === 'loading') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-white">
        <div className="h-8 w-8 border-2 border-white/40 border-t-white rounded-full animate-spin mb-3" />
        <p>Carregando vídeo...</p>
      </div>
    );
  }

  if (state === 'processing') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-white text-center px-4">
        <div className="h-8 w-8 border-2 border-white/40 border-t-white rounded-full animate-spin mb-3" />
        <p className="font-medium">Processando vídeo...</p>
        <p className="text-sm text-white/70 mt-1">
          O vídeo está sendo preparado{progress ? ` (${Math.round(progress)}%)` : ''}. Isso pode levar alguns minutos.
        </p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-white text-center px-4">
        <Video className="h-12 w-12 mx-auto mb-2" />
        <p>Não foi possível carregar o vídeo.</p>
        <p className="text-sm text-white/70 mt-1">Tente novamente em instantes.</p>
      </div>
    );
  }

  // Reprodução direta via <video> nativo (preferida): MP4 assinado do Bunny.
  if (playData && playData.mp4_url) {
    return (
      <video
        ref={videoRef}
        controls
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        className="w-full h-full rounded-lg"
        onLoadedMetadata={() => videoRef.current && report(videoRef.current)}
        onTimeUpdate={handleTimeUpdate}
        onSeeking={handleSeeking}
        onError={handleVideoError}
      >
        <source src={playData.mp4_url} type="video/mp4" />
        Seu navegador não suporta o elemento de vídeo.
      </video>
    );
  }

  // Último recurso: player embutido (iframe), caso não haja MP4 disponível.
  return (
    <iframe
      src={playData.embed_url}
      title="Aula em vídeo"
      className="w-full h-full rounded-lg"
      style={{ border: 'none' }}
      loading="lazy"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
      allowFullScreen
      onContextMenu={(e) => e.preventDefault()}
    />
  );
};

// Player de YouTube controlado via IFrame API: rastreia o tempo assistido e
// impede o avanço além do ponto já assistido.
const ControlledYouTube = ({ url, onProgress }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const pollRef = useRef(null);

  const getVideoId = (u) => {
    try {
      if (u.includes('watch?v=')) return u.split('watch?v=')[1].split('&')[0];
      if (u.includes('youtu.be/')) return u.split('youtu.be/')[1].split('?')[0];
      if (u.includes('/embed/')) return u.split('/embed/')[1].split('?')[0];
    } catch (e) { /* noop */ }
    return u;
  };

  useEffect(() => {
    let cancelled = false;

    const startPolling = () => {
      pollRef.current = setInterval(() => {
        const p = playerRef.current;
        if (!p || !p.getCurrentTime) return;
        const cur = p.getCurrentTime();
        const dur = p.getDuration ? p.getDuration() : 0;
        if (cur > maxWatchedRef.current + 1.5) {
          p.seekTo(maxWatchedRef.current, true); // bloqueia avanço
        } else if (cur > maxWatchedRef.current) {
          maxWatchedRef.current = cur;
        }
        const pct = dur > 0 ? Math.min(100, (maxWatchedRef.current / dur) * 100) : 0;
        onProgress && onProgress({ watchedSeconds: maxWatchedRef.current, durationSeconds: dur, watchedPct: pct });
      }, 800);
    };

    const init = () => {
      if (cancelled || !containerRef.current || !window.YT || !window.YT.Player) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: getVideoId(url),
        playerVars: { rel: 0, modestbranding: 1, controls: 1 },
        events: { onReady: startPolling },
      });
    };

    if (window.YT && window.YT.Player) {
      init();
    } else {
      if (!document.getElementById('yt-iframe-api')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev && prev(); init(); };
    }

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      try { playerRef.current && playerRef.current.destroy(); } catch (e) { /* noop */ }
    };
  }, [url]);

  return (
    <div className="w-full h-full">
      <div ref={containerRef} className="w-full h-full rounded-lg" />
    </div>
  );
};

// Course Player Component
// `guest` ({ session }) ativa o modo convidado (link público): mesmas telas, mas
// as chamadas vão para /api/lms-guest/{session}/... e não exigem login.
const CoursePlayer = ({ courseId, onClose, guest, onGuestComplete }) => {
  const lmsUrl = (path) => (guest
    ? `${API}/api/lms-guest/${guest.session}${path}`
    : `${API}/api/lms${path}`);
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [quizData, setQuizData] = useState({
    attemptId: null,
    questions: [],
    currentQuestion: 0,
    answers: {},
    isActive: false
  });
  // Anti-skip: progresso real de mídia e tempo de permanência na aula atual
  const [mediaProgress, setMediaProgress] = useState({ watchedPct: 0, watchedSeconds: 0, durationSeconds: 0 });
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const lastSaveRef = useRef(0);
  // Pesquisa de reação ao concluir o curso
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [externalCertUrl, setExternalCertUrl] = useState('');
  const [uploadingCert, setUploadingCert] = useState(false);
  
  // Retorna true se a pesquisa de reação foi aberta (nesse caso o fechamento
  // do player fica a cargo do onOpenChange do ReactionSurveyDialog).
  const maybeOpenReactionSurvey = async () => {
    try {
      if (guest || !course?.reaction_survey_enabled) return false;
      const res = await axios.get(`${API}/api/lms/cursos/${courseId}/pesquisa-reacao/minha`);
      if (!res.data || !res.data.submitted) {
        setSurveyOpen(true);
        return true;
      }
      return false;
    } catch (e) {
      // se falhar a verificação, ainda assim abre a pesquisa
      setSurveyOpen(true);
      return true;
    }
  };

  useEffect(() => {
    if (courseId) {
      loadCourseData();
    }
  }, [courseId]);

  // Reinicia o rastreamento (progresso de mídia + tempo) ao trocar de aula e
  // mantém um cronômetro de permanência usado para liberar a conclusão.
  useEffect(() => {
    setMediaProgress({ watchedPct: 0, watchedSeconds: 0, durationSeconds: 0 });
    setElapsedSeconds(0);
    lastSaveRef.current = 0;
    if (!currentLesson) return;
    const timer = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [currentLesson?.id]);

  const loadCourseData = async () => {
    try {
      setLoading(true);
      
      // Load course details
      const courseResponse = await axios.get(lmsUrl(`/courses/${courseId}`));
      setCourse(courseResponse.data);

      if (courseResponse.data.lessons) {
        setLessons(courseResponse.data.lessons);
        // Set first lesson as current
        if (courseResponse.data.lessons.length > 0) {
          setCurrentLesson(courseResponse.data.lessons[0]);
        }
      }

      // Load user enrollment data (each item is a course with a nested `enrollment` object)
      const enrollmentResponse = await axios.get(lmsUrl('/my-courses'));
      const userCourse = enrollmentResponse.data.find(e => e.id === courseId);
      setEnrollment(userCourse?.enrollment);
      
    } catch (error) {
      console.error('Error loading course data:', error);
      toast.error('Erro ao carregar dados do curso');
    } finally {
      setLoading(false);
    }
  };

  const handleLessonComplete = async (lessonId, timeSpentMinutes = 0) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(lmsUrl(`/lessons/${lessonId}/progress`), {
        completion_percentage: 100,
        time_spent: Math.max(0, Math.ceil(timeSpentMinutes))
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast.success('Aula concluída!');
      
      // Refresh course data
      await loadCourseData();
      
    } catch (error) {
      console.error('Error completing lesson:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Erro ao marcar aula como concluída';
      toast.error(errorMsg);
    }
  };

  // Salva o progresso parcial assistido (para a barra do curso / retomada),
  // limitado a uma chamada a cada ~15s para não sobrecarregar o backend.
  const handleMediaProgress = (p) => {
    setMediaProgress(p);
    const now = Date.now();
    if (currentLesson && now - lastSaveRef.current > 15000 && p.watchedPct > 0 && p.watchedPct < 100) {
      lastSaveRef.current = now;
      const token = localStorage.getItem('token');
      axios.put(lmsUrl(`/lessons/${currentLesson.id}/progress`), {
        completion_percentage: Math.min(99, Math.floor(p.watchedPct)),
        time_spent: Math.ceil((p.watchedSeconds || 0) / 60)
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  };

  // Define se a aula pode ser concluída e a mensagem de orientação ao aluno.
  const getCompletionGate = (lesson) => {
    if (!lesson) return { canComplete: false, reason: '' };
    if (lesson.completed) return { canComplete: true, reason: '' };
    const minMinutes = lesson.duration_minutes || 0;
    const elapsedMin = elapsedSeconds / 60;
    const timeOk = minMinutes <= 0 || elapsedMin >= minMinutes;
    if (lesson.content_type === 'video') {
      if (mediaProgress.watchedPct < 95) {
        return { canComplete: false, reason: `Assista a pelo menos 95% do vídeo para concluir (assistido: ${Math.floor(mediaProgress.watchedPct)}%).` };
      }
      return { canComplete: true, reason: '' };
    }
    if (!lessonRequiresDuration(lesson.content_type)) {
      return { canComplete: true, reason: '' };
    }
    if (!timeOk) {
      return { canComplete: false, reason: `Dedique pelo menos ${minMinutes} min a este conteúdo para concluir (tempo: ${Math.floor(elapsedMin)} min).` };
    }
    return { canComplete: true, reason: '' };
  };
  const uploadExternalCertificate = async (file) => {
    if (file.type !== 'application/pdf') {
      toast.error('Envie o certificado em PDF.');
      return;
    }
    const CHUNK_SIZE = 5 * 1024 * 1024;
    try {
      setUploadingCert(true);
      const token = localStorage.getItem('token');
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
        const fd = new FormData();
        fd.append('file', chunk);
        fd.append('chunk_index', i);
        fd.append('total_chunks', totalChunks);
        fd.append('upload_id', uploadId);
        fd.append('filename', file.name);
        fd.append('file_type', 'pdf');
        await axios.post(`${API}/api/lms/upload-chunk`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      }
      const finalize = new FormData();
      finalize.append('upload_id', uploadId);
      finalize.append('filename', file.name);
      finalize.append('total_chunks', totalChunks);
      finalize.append('file_type', 'pdf');
      const resp = await axios.post(`${API}/api/lms/finalize-upload`, finalize, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExternalCertUrl(resp.data.url);
      toast.success('Certificado anexado!');
    } catch (error) {
      console.error('Erro ao anexar certificado:', error);
      toast.error('Erro ao anexar certificado');
    } finally {
      setUploadingCert(false);
    }
  };

  const handleCompleteCourse = async () => {
    try {
      // Curso externo conclui via certificado anexado; interno exige todas as aulas
      if (course?.is_external) {
        if (!externalCertUrl) {
          toast.error('Anexe seu certificado antes de concluir o curso');
          return;
        }
      } else {
        const allCompleted = lessons.every(lesson => lesson.completed);
        if (!allCompleted) {
          toast.error('Complete todas as aulas antes de concluir o curso');
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        lmsUrl(`/courses/${courseId}/complete`),
        course?.is_external ? { external_certificate_url: externalCertUrl } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (guest) {
        // Convidado: o certificado é público (token) — a página pública mostra o download
        toast.success('🎉 Parabéns! Curso concluído com sucesso!');
        onGuestComplete && onGuestComplete(response.data);
        return;
      }

      if (course?.is_external) {
        // Curso externo: o certificado já foi anexado pelo aluno, não há
        // certificado "Grid" para gerar/baixar aqui — só confirma a conclusão.
        toast.success('🎉 Parabéns! Curso concluído com sucesso!');
        await loadCourseData();
      } else if (response.data.certificate_url) {
        toast.success('🎉 Parabéns! Curso concluído com sucesso!');

        // Baixar certificado automaticamente (via blob, pois o endpoint exige
        // autenticação e window.open não envia o header Authorization)
        try {
          const pdfResponse = await axios.get(response.data.certificate_url, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
          });
          const blobUrl = window.URL.createObjectURL(new Blob([pdfResponse.data], { type: 'application/pdf' }));
          window.open(blobUrl, '_blank');
        } catch (downloadError) {
          console.error('Error downloading certificate:', downloadError);
          toast.error('Não foi possível baixar o certificado automaticamente.');
        }

        // Recarregar dados do curso
        await loadCourseData();
      } else {
        toast.success('Curso concluído com sucesso!');
      }

      // Abrir pesquisa de reação se habilitada e ainda não respondida; caso
      // contrário, já volta para a tela de Meus Cursos em vez de deixar o
      // aluno "preso" na tela do curso concluído.
      const surveyOpened = await maybeOpenReactionSurvey();
      if (!surveyOpened) {
        onClose();
      }

    } catch (error) {
      console.error('Error completing course:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Erro ao concluir curso';
      toast.error(errorMsg);
    }
  };


  const handleStartQuiz = async (lessonId) => {
    try {
      const response = await axios.post(lmsUrl(`/lessons/${lessonId}/start-quiz`));
      
      if (response.data.questions && response.data.questions.length > 0) {
        // Start quiz with questions
        setQuizData({
          attemptId: response.data.attempt_id,
          questions: response.data.questions,
          currentQuestion: 0,
          answers: {},
          isActive: true,
          lessonId: lessonId
        });
        toast.success('Quiz iniciado!');
      } else {
        toast.error('Este quiz não possui perguntas configuradas');
      }
      
    } catch (error) {
      console.error('Error starting quiz:', error);
      toast.error('Erro ao iniciar quiz');
    }
  };

  const handleQuizAnswerSubmit = (questionId, answer) => {
    setQuizData(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: answer
      }
    }));
  };

  const handleQuizComplete = async () => {
    try {
      const response = await axios.post(lmsUrl(`/quiz-attempts/${quizData.attemptId}/submit`), {
        answers: quizData.answers,
        time_spent_minutes: 15 // Could be calculated based on actual time
      });
      
      const { score, max_score, percentage, passed, needs_review, passing_grade } = response.data;
      
      toast.success(`Quiz concluído! Nota: ${score}/${max_score} (${percentage}%)`);
      
      if (passed) {
        toast.success('Parabéns! Você passou no quiz!');
        // Mark lesson as completed (quiz aprovado é o gate; usa o tempo mínimo da aula)
        const quizLesson = lessons.find((l) => l.id === quizData.lessonId);
        await handleLessonComplete(quizData.lessonId, quizLesson?.duration_minutes || 0);
      } else if (needs_review) {
        toast.info('Sua avaliação contém questões dissertativas e ficará pendente de correção manual.');
      } else {
        toast.error(`Você precisa obter pelo menos ${passing_grade || 70}% para passar.`);
      }
      
      // Reset quiz data
      setQuizData({
        attemptId: null,
        questions: [],
        currentQuestion: 0,
        answers: {},
        isActive: false,
        lessonId: null
      });
      
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Erro ao enviar respostas do quiz');
    }
  };

  const renderLessonContent = (lesson) => {
    if (!lesson) return null;

    switch (lesson.content_type) {
      case 'video': {
        const videoGate = getCompletionGate(lesson);
        const isYouTube = lesson.content_url && (lesson.content_url.includes('youtube.com') || lesson.content_url.includes('youtu.be'));
        const isBunny = lesson.content_url && lesson.content_url.startsWith('bunny://');
        return (
          <div className="space-y-4">
            <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center">
              {lesson.content_url ? (
                <div className="w-full h-full">
                  {isYouTube ? (
                    <ControlledYouTube url={lesson.content_url} onProgress={handleMediaProgress} />
                  ) : isBunny ? (
                    <BunnyVideo key={lesson.content_url} videoId={lesson.content_url.replace('bunny://', '')} onProgress={handleMediaProgress} guestSession={guest?.session} />
                  ) : (
                    <ControlledVideo src={resolveMediaUrl(lesson.content_url)} onProgress={handleMediaProgress} />
                  )}
                </div>
              ) : (
                <div className="text-white text-center">
                  <Video className="h-12 w-12 mx-auto mb-2" />
                  <p>Vídeo não disponível</p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                {lesson.completed
                  ? '✅ Aula concluída.'
                  : (videoGate.reason || 'Você já pode concluir esta aula.')}
              </p>
              <Button
                onClick={() => handleLessonComplete(lesson.id, Math.max(elapsedSeconds, mediaProgress.watchedSeconds) / 60)}
                disabled={lesson.completed || !videoGate.canComplete}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {lesson.completed ? 'Concluída' : 'Marcar como Concluída'}
              </Button>
            </div>
          </div>
        );
      }

      case 'pdf':
      case 'document': {
        const docGate = getCompletionGate(lesson);
        const docUrl = resolveMediaUrl(lesson.content_url);
        return (
          <div className="space-y-4">
            {lesson.content_url ? (
              <div className="w-full">
                <div className="bg-white rounded-lg border overflow-hidden" style={{ height: '600px' }}>
                  <iframe
                    src={docUrl}
                    className="w-full h-full"
                    title="PDF Document"
                    style={{ border: 'none' }}
                  />
                </div>
                <div className="mt-2 text-sm text-gray-600 flex items-center justify-between">
                  <span>📄 Documento PDF carregado</span>
                  <Button 
                    onClick={() => window.open(docUrl, '_blank')}
                    variant="outline"
                    size="sm"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Abrir em Nova Aba
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Documento não disponível</p>
              </div>
            )}
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                {lesson.completed
                  ? '✅ Aula concluída.'
                  : (docGate.reason || 'Você já pode concluir esta aula.')}
              </p>
              <Button
                onClick={() => handleLessonComplete(lesson.id, elapsedSeconds / 60)}
                disabled={lesson.completed || !docGate.canComplete}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {lesson.completed ? 'Concluída' : 'Marcar como Concluída'}
              </Button>
            </div>
          </div>
        );
      }

      case 'text': {
        const textGate = getCompletionGate(lesson);
        return (
          <div className="space-y-4">
            <div className="prose max-w-none">
              <div className="bg-white p-6 rounded-lg border">
                {lesson.content_data?.text ? (
                  <div dangerouslySetInnerHTML={{ __html: lesson.content_data.text.replace(/\n/g, '<br>') }} />
                ) : (
                  <p className="text-gray-500">Conteúdo textual não disponível</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                {lesson.completed
                  ? '✅ Aula concluída.'
                  : (textGate.reason || 'Você já pode concluir esta aula.')}
              </p>
              <Button
                onClick={() => handleLessonComplete(lesson.id, elapsedSeconds / 60)}
                disabled={lesson.completed || !textGate.canComplete}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {lesson.completed ? 'Concluída' : 'Marcar como Concluída'}
              </Button>
            </div>
          </div>
        );
      }

      case 'quiz':
        // If quiz is active, show quiz interface
        if (quizData.isActive && quizData.questions.length > 0) {
          return (
            <div className="space-y-4">
              <QuizInterface 
                quizData={quizData}
                onAnswerSubmit={handleQuizAnswerSubmit}
                onQuizComplete={handleQuizComplete}
                onQuizExit={() => setQuizData({...quizData, isActive: false})}
              />
            </div>
          );
        }
        
        // Default quiz start screen
        return (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <HelpCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Quiz de Avaliação</h3>
              <p className="text-gray-600 mb-4">Esta aula contém um quiz para testar seus conhecimentos.</p>
              <Button onClick={() => handleStartQuiz(lesson.id)}>
                <PlayCircle className="h-4 w-4 mr-2" />
                Iniciar Quiz
              </Button>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <FileImage className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Tipo de conteúdo não suportado</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Carregando curso...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col md:flex-row">
      {/* Sidebar - Lessons (no celular fica em cima, com altura limitada) */}
      <div className="w-full md:w-80 flex-shrink-0 max-h-[42vh] md:max-h-none bg-white border-b md:border-b-0 md:border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            {!guest && (
              <Button variant="outline" size="sm" onClick={onClose}>
                ← Voltar
              </Button>
            )}
            <Badge variant="outline">
              {course?.is_external
                ? (enrollment?.status === 'completed' ? 'Concluído' : 'Pendente')
                : `${lessons.filter(l => l.completed).length} / ${lessons.length} concluídas`}
            </Badge>
          </div>
          <h2 className="font-bold text-lg">{course?.title}</h2>
          {enrollment && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Progresso</span>
                <span>{enrollment.progress_percentage || 0}%</span>
              </div>
              <Progress value={enrollment.progress_percentage || 0} className="mt-1" />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {!course?.is_external && (
            <div className="p-4 space-y-2">
              <h3 className="font-semibold text-sm text-gray-700 mb-3">AULAS</h3>
              {lessons.map((lesson, index) => (
                <div
                  key={lesson.id}
                  className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                    currentLesson?.id === lesson.id
                      ? 'bg-blue-50 border-blue-200'
                      : 'hover:bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => setCurrentLesson(lesson)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {lesson.completed ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        {lesson.content_type === 'video' && <Video className="h-3 w-3" />}
                        {lesson.content_type === 'pdf' && <FileText className="h-3 w-3" />}
                        {lesson.content_type === 'text' && <Type className="h-3 w-3" />}
                        {lesson.content_type === 'quiz' && <HelpCircle className="h-3 w-3" />}
                        <span className="text-xs text-gray-500">
                          Aula {index + 1}
                        </span>
                      </div>
                      <p className="text-sm font-medium truncate">{lesson.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{lesson.duration_minutes} min</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Botão Concluir Curso (no celular fica na barra fixa de baixo) */}
          <div className="hidden md:block p-4 border-t border-gray-200">
            <Button
              className="w-full"
              disabled={course?.is_external ? !externalCertUrl : !lessons.every(lesson => lesson.completed)}
              onClick={() => handleCompleteCourse()}
            >
              <Award className="h-4 w-4 mr-2" />
              Concluir Curso e Obter Certificado
            </Button>
            {course?.is_external
              ? !externalCertUrl && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Anexe seu certificado para concluir o curso
                  </p>
                )
              : !lessons.every(lesson => lesson.completed) && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Complete todas as aulas para concluir o curso
                  </p>
                )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-0 min-w-0 flex flex-col">
        <div className="bg-white border-b border-gray-200 p-3 md:p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl font-bold">
                {course?.is_external ? course?.title : currentLesson?.title}
              </h1>
              <p className="text-gray-600">
                {course?.is_external ? course?.description : currentLesson?.description}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant="outline">
                {currentLesson?.content_type?.toUpperCase()}
              </Badge>
              <span className="text-sm text-gray-500">
                {currentLesson?.duration_minutes} min
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 p-3 md:p-6 overflow-y-auto">
          {course?.is_external ? (
            <div className="max-w-2xl mx-auto mt-8">
              <div className="bg-white border rounded-lg p-8 text-center space-y-4">
                <ExternalLink className="h-12 w-12 text-blue-600 mx-auto" />
                <h3 className="text-lg font-semibold">Curso Externo</h3>
                <p className="text-gray-600">
                  Este curso é realizado em uma plataforma externa. Clique no botão
                  abaixo para acessar o conteúdo. Ao concluir, anexe seu certificado
                  na área de certificados.
                </p>
                {course.external_url ? (
                  <Button
                    onClick={() => window.open(course.external_url, '_blank', 'noopener,noreferrer')}
                    className="mt-2"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Acessar Curso
                  </Button>
                ) : (
                  <p className="text-sm text-amber-600">
                    Link do curso não cadastrado. Contate o administrador.
                  </p>
                )}
                {course.external_certificate_url && (
                  <div className="pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => window.open(resolveMediaUrl(course.external_certificate_url), '_blank')}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Material / Modelo do Curso
                    </Button>
                  </div>
                )}

                <div className="pt-4 border-t space-y-3">
                  <p className="text-sm text-gray-600">
                    Após concluir o curso na plataforma externa, anexe seu certificado
                    (PDF) para liberar a conclusão e gerar o certificado do Grid.
                  </p>
                  <input
                    type="file"
                    id="student_external_cert"
                    accept=".pdf"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) await uploadExternalCertificate(file);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('student_external_cert')?.click()}
                    disabled={uploadingCert}
                  >
                    <UploadIcon className="h-4 w-4 mr-2" />
                    {uploadingCert ? 'Enviando...' : 'Anexar meu certificado'}
                  </Button>
                  {externalCertUrl && (
                    <p className="text-sm text-green-600 flex items-center justify-center gap-1">
                      <CheckCircle className="h-4 w-4" /> Certificado anexado
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            renderLessonContent(currentLesson)
          )}
        </div>

        {/* Barra fixa de conclusão — só no celular */}
        <div className="md:hidden bg-white border-t border-gray-200 p-3">
          <Button
            className="w-full"
            disabled={course?.is_external ? !externalCertUrl : !lessons.every(lesson => lesson.completed)}
            onClick={() => handleCompleteCourse()}
          >
            <Award className="h-4 w-4 mr-2" />
            Concluir Curso e Obter Certificado
          </Button>
          {!course?.is_external && !lessons.every(lesson => lesson.completed) && (
            <p className="text-xs text-gray-500 mt-1 text-center">
              Complete todas as aulas para concluir o curso
            </p>
          )}
        </div>
      </div>

      {!guest && <ReactionSurveyDialog
        open={surveyOpen}
        onOpenChange={(open) => {
          setSurveyOpen(open);
          // A pesquisa só é aberta pelo fluxo de conclusão de curso, então
          // fechá-la (enviando ou não) significa que o curso já terminou.
          if (!open) onClose();
        }}
        courseId={courseId}
        courseTitle={course?.title}
      />}
    </div>
  );
};

// Course Content Editor Component
const CourseContentEditor = ({ course, onClose, onSave }) => {
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);

  useEffect(() => {
    loadLessons();
  }, [course.id]);

  const loadLessons = async () => {
    try {
      const response = await axios.get(`${API}/api/lms/courses/${course.id}`);
      setLessons(response.data.lessons || []);
    } catch (error) {
      console.error('Error loading lessons:', error);
      toast.error('Erro ao carregar aulas');
    }
  };

  const handleEditLesson = async (lesson) => {
    setEditingLesson({ ...lesson });
  };

  const handleSaveLesson = async () => {
    try {
      setLoading(true);
      
      await axios.put(`${API}/api/lms/lessons/${editingLesson.id}`, editingLesson);
      toast.success('Aula atualizada com sucesso!');
      
      await loadLessons();
      setEditingLesson(null);
    } catch (error) {
      console.error('Error updating lesson:', error);
      toast.error('Erro ao atualizar aula');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta aula?')) return;
    
    try {
      setLoading(true);
      
      await axios.delete(`${API}/api/lms/lessons/${lessonId}`);
      toast.success('Aula excluída com sucesso!');
      
      await loadLessons();
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error('Erro ao excluir aula');
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuiz = async (lesson) => {
    try {
      const response = await axios.get(`${API}/api/lms/lessons/${lesson.id}/quiz-questions`);
      setEditingQuiz({
        lesson: lesson,
        questions: response.data || []
      });
    } catch (error) {
      console.error('Error loading quiz questions:', error);
      setEditingQuiz({
        lesson: lesson,
        questions: []
      });
    }
  };

  const handleSaveQuizQuestion = async (question, questionIndex) => {
    try {
      setLoading(true);
      
      if (question.id) {
        // Update existing question
        await axios.put(`${API}/api/lms/quiz-questions/${question.id}`, question);
      } else {
        // Create new question
        await axios.post(`${API}/api/lms/lessons/${editingQuiz.lesson.id}/quiz-questions`, question);
      }
      
      toast.success('Pergunta salva com sucesso!');
      
      // Reload quiz questions
      const response = await axios.get(`${API}/api/lms/lessons/${editingQuiz.lesson.id}/quiz-questions`);
      setEditingQuiz(prev => ({
        ...prev,
        questions: response.data || []
      }));
    } catch (error) {
      console.error('Error saving quiz question:', error);
      toast.error('Erro ao salvar pergunta');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuizQuestion = async (questionId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta pergunta?')) return;
    
    try {
      setLoading(true);
      
      await axios.delete(`${API}/api/lms/quiz-questions/${questionId}`);
      toast.success('Pergunta excluída com sucesso!');
      
      // Reload quiz questions
      const response = await axios.get(`${API}/api/lms/lessons/${editingQuiz.lesson.id}/quiz-questions`);
      setEditingQuiz(prev => ({
        ...prev,
        questions: response.data || []
      }));
    } catch (error) {
      console.error('Error deleting quiz question:', error);
      toast.error('Erro ao excluir pergunta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Editar Conteúdo: {course.title}</CardTitle>
              <CardDescription>Gerencie aulas, quiz e conteúdo do curso</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onClose}>
              ×
            </Button>
          </div>
        </CardHeader>
        <CardContent className="max-h-[70vh] overflow-y-auto">
          <div className="space-y-6">
            {/* Lessons List */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Aulas do Curso</h3>
              <div className="space-y-4">
                {lessons.map((lesson, index) => (
                  <Card key={lesson.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {lesson.content_type === 'video' && <Video className="h-4 w-4" />}
                          {lesson.content_type === 'pdf' && <FileText className="h-4 w-4" />}
                          {lesson.content_type === 'text' && <Type className="h-4 w-4" />}
                          {lesson.content_type === 'quiz' && <HelpCircle className="h-4 w-4" />}
                          <span className="font-medium">Aula {index + 1}: {lesson.title}</span>
                          <Badge variant="outline">{lesson.content_type.toUpperCase()}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{lesson.description}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>{lesson.duration_minutes} min</span>
                          {lesson.content_url && (
                            <span className="flex items-center space-x-1">
                              <Link className="h-3 w-3" />
                              <span>Link: {lesson.content_url.substring(0, 50)}...</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleEditLesson(lesson)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {lesson.content_type === 'quiz' && (
                          <Button size="sm" variant="secondary" onClick={() => handleEditQuiz(lesson)}>
                            <HelpCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteLesson(lesson.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Lesson Edit Modal */}
          {editingLesson && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
              <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Editar Aula</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setEditingLesson(null)}>
                      ×
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="lesson_title">Título</Label>
                    <Input
                      id="lesson_title"
                      value={editingLesson.title}
                      onChange={(e) => setEditingLesson({...editingLesson, title: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="lesson_description">Descrição</Label>
                    <Textarea
                      id="lesson_description"
                      value={editingLesson.description}
                      onChange={(e) => setEditingLesson({...editingLesson, description: e.target.value})}
                      rows={3}
                    />
                  </div>

                  {(editingLesson.content_type === 'video' || editingLesson.content_type === 'pdf') && (
                    <div>
                      <Label htmlFor="lesson_url">URL do Conteúdo</Label>
                      <Input
                        id="lesson_url"
                        value={editingLesson.content_url || ''}
                        onChange={(e) => setEditingLesson({...editingLesson, content_url: e.target.value})}
                        placeholder="https://..."
                      />
                    </div>
                  )}

                  {editingLesson.content_type === 'text' && (
                    <div>
                      <Label htmlFor="lesson_text">Conteúdo de Texto</Label>
                      <Textarea
                        id="lesson_text"
                        value={editingLesson.content_data?.text || ''}
                        onChange={(e) => setEditingLesson({
                          ...editingLesson, 
                          content_data: {...editingLesson.content_data, text: e.target.value}
                        })}
                        rows={5}
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="lesson_duration">{getLessonDurationLabel(editingLesson.content_type)}</Label>
                    <Input
                      id="lesson_duration"
                      type="number"
                      min="0"
                      value={editingLesson.duration_minutes}
                      onChange={(e) => setEditingLesson({...editingLesson, duration_minutes: parseInt(e.target.value) || 0})}
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setEditingLesson(null)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSaveLesson} disabled={loading}>
                      {loading ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Quiz Edit Modal */}
          {editingQuiz && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
              <Card className="w-full max-w-4xl max-h-[80vh] overflow-y-auto">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Editar Quiz: {editingQuiz.lesson.title}</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setEditingQuiz(null)}>
                      ×
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <h4 className="font-semibold">Perguntas do Quiz ({(editingQuiz.questions || []).length})</h4>
                    
                    {(editingQuiz.questions || []).map((question, index) => (
                      <Card key={question.id || index} className="p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-3">
                          <span className="font-medium">Pergunta {index + 1}</span>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteQuizQuestion(question.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-3">
                          <Input
                            placeholder="Digite a pergunta..."
                            value={question.question_text || ''}
                            onChange={(e) => {
                              const updatedQuestions = [...(editingQuiz.questions || [])];
                              updatedQuestions[index] = {...question, question_text: e.target.value};
                              setEditingQuiz({...editingQuiz, questions: updatedQuestions});
                            }}
                          />
                          
                          {question.question_type === 'multiple_choice' && (
                            <div>
                              <Label>Opções de Resposta</Label>
                              {(question.options || [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }]).map((option, optIndex) => (
                                <div key={optIndex} className="flex space-x-2 mt-2">
                                  <Input
                                    placeholder={`Opção ${optIndex + 1}...`}
                                    value={option.text || ''}
                                    onChange={(e) => {
                                      const updatedQuestions = [...(editingQuiz.questions || [])];
                                      const updatedOptions = [...(question.options || [])];
                                      updatedOptions[optIndex] = { ...option, text: e.target.value };
                                      updatedQuestions[index] = {...question, options: updatedOptions};
                                      setEditingQuiz({...editingQuiz, questions: updatedQuestions});
                                    }}
                                  />
                                  <input
                                    type="radio"
                                    name={`correct_${index}`}
                                    checked={question.correct_answer === option.text}
                                    onChange={() => {
                                      const updatedQuestions = [...(editingQuiz.questions || [])];
                                      const updatedOptions = (question.options || []).map((o) => ({ ...o, is_correct: o.text === option.text }));
                                      updatedQuestions[index] = {...question, options: updatedOptions, correct_answer: option.text};
                                      setEditingQuiz({...editingQuiz, questions: updatedQuestions});
                                    }}
                                    title="Resposta correta"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <Button 
                            size="sm" 
                            onClick={() => handleSaveQuizQuestion(question, index)}
                            disabled={loading}
                          >
                            {loading ? 'Salvando...' : 'Salvar Pergunta'}
                          </Button>
                        </div>
                      </Card>
                    ))}
                    
                    <Button
                      onClick={() => {
                        const newQuestion = {
                          question_text: '',
                          question_type: 'multiple_choice',
                          options: [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }],
                          correct_answer: '',
                          explanation: '',
                          points: 1,
                          order: (editingQuiz.questions || []).length + 1
                        };
                        setEditingQuiz({
                          ...editingQuiz,
                          questions: [...(editingQuiz.questions || []), newQuestion]
                        });
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Pergunta
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Quiz Interface Component
const QuizInterface = ({ quizData, onAnswerSubmit, onQuizComplete, onQuizExit }) => {
  const [selectedAnswers, setSelectedAnswers] = useState(quizData.answers || {});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Sync selectedAnswers with quizData.answers
  useEffect(() => {
    setSelectedAnswers(quizData.answers || {});
  }, [quizData.answers]);

  const currentQuestion = quizData.questions[currentQuestionIndex];
  const totalQuestions = quizData.questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const isCurrentAnswered = (() => {
    if (!currentQuestion) return false;
    const a = selectedAnswers[currentQuestion.id];
    if (currentQuestion.question_type === 'ordering') return true; // ordem padrão exibida
    if (currentQuestion.question_type === 'matching') {
      const lefts = currentQuestion.matching_lefts || [];
      return lefts.length > 0 && lefts.every(l => a && a[l]);
    }
    if (Array.isArray(a)) return a.length > 0;
    return a !== undefined && a !== null && String(a).trim() !== '';
  })();

  const handleAnswerSelect = (answer) => {
    const questionId = currentQuestion.id;
    const updatedAnswers = {
      ...selectedAnswers,
      [questionId]: answer
    };
    setSelectedAnswers(updatedAnswers);
    onAnswerSubmit(questionId, answer);
  };

  const handleNextQuestion = () => {
    if (!isLastQuestion) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleFinishQuiz = () => {
    // Garantir que questões de ordenação não respondidas usem a ordem exibida
    quizData.questions.forEach(q => {
      if (q.question_type === 'ordering' && !Array.isArray(selectedAnswers[q.id])) {
        onAnswerSubmit(q.id, q.ordering_items || []);
      }
    });

    // Check if all questions are answered
    const unansweredQuestions = quizData.questions.filter(q => {
      const a = selectedAnswers[q.id];
      if (q.question_type === 'ordering') return false; // sempre tem ordem padrão
      if (q.question_type === 'matching') {
        const lefts = q.matching_lefts || [];
        return lefts.some(l => !(a && a[l]));
      }
      if (Array.isArray(a)) return a.length === 0;
      return a === undefined || a === null || String(a).trim() === '';
    });
    
    if (unansweredQuestions.length > 0) {
      toast.error(`Por favor, responda todas as perguntas. ${unansweredQuestions.length} pergunta(s) não respondida(s).`);
      return;
    }
    
    onQuizComplete();
  };

  if (!currentQuestion) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Carregando pergunta...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Quiz Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Quiz de Avaliação</h3>
          <Button variant="outline" size="sm" onClick={onQuizExit}>
            <X className="h-4 w-4 mr-2" />
            Sair
          </Button>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Pergunta {currentQuestionIndex + 1} de {totalQuestions}</span>
            <span>{Math.round(progress)}% concluído</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Question */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-6 text-gray-800 leading-relaxed">
          {currentQuestion.question_text}
        </h4>

        {/* Answer Options */}
        <div className="space-y-3">
          {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options && (
            <>
              {currentQuestion.options.map((option, index) => {
                const optionText = typeof option === 'string' ? option : option.text;
                return (
                  <label
                    key={index}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedAnswers[currentQuestion.id] === optionText
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question_${currentQuestion.id}`}
                      value={optionText}
                      checked={selectedAnswers[currentQuestion.id] === optionText}
                      onChange={() => handleAnswerSelect(optionText)}
                      className="mr-3"
                    />
                    <span className="text-gray-700">{optionText}</span>
                  </label>
                );
              })}
            </>
          )}

          {currentQuestion.question_type === 'true_false' && (
            <>
              <label
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedAnswers[currentQuestion.id] === 'true'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name={`question_${currentQuestion.id}`}
                  value="true"
                  checked={selectedAnswers[currentQuestion.id] === 'true'}
                  onChange={() => handleAnswerSelect('true')}
                  className="mr-3"
                />
                <span className="text-gray-700">Verdadeiro</span>
              </label>
              <label
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedAnswers[currentQuestion.id] === 'false'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name={`question_${currentQuestion.id}`}
                  value="false"
                  checked={selectedAnswers[currentQuestion.id] === 'false'}
                  onChange={() => handleAnswerSelect('false')}
                  className="mr-3"
                />
                <span className="text-gray-700">Falso</span>
              </label>
            </>
          )}

          {currentQuestion.question_type === 'short_answer' && (
            <textarea
              className="w-full p-4 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
              rows={4}
              placeholder="Digite sua resposta..."
              value={selectedAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerSelect(e.target.value)}
            />
          )}

          {currentQuestion.question_type === 'essay' && (
            <textarea
              className="w-full p-4 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
              rows={8}
              placeholder="Escreva sua resposta dissertativa..."
              value={selectedAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerSelect(e.target.value)}
            />
          )}

          {currentQuestion.question_type === 'ordering' && (() => {
            const items = Array.isArray(selectedAnswers[currentQuestion.id])
              ? selectedAnswers[currentQuestion.id]
              : (currentQuestion.ordering_items || []);
            const move = (from, to) => {
              if (to < 0 || to >= items.length) return;
              const next = [...items];
              const [moved] = next.splice(from, 1);
              next.splice(to, 0, moved);
              handleAnswerSelect(next);
            };
            return (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 mb-2">Ordene os itens usando as setas.</p>
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center p-3 border border-gray-200 rounded-lg bg-gray-50">
                    <span className="text-sm font-semibold text-gray-500 mr-3">{idx + 1}.</span>
                    <span className="flex-1 text-gray-700">{item}</span>
                    <div className="flex flex-col">
                      <button type="button" onClick={() => move(idx, idx - 1)} disabled={idx === 0} className="text-gray-500 hover:text-blue-600 disabled:opacity-30">
                        <ChevronLeft className="h-4 w-4 rotate-90" />
                      </button>
                      <button type="button" onClick={() => move(idx, idx + 1)} disabled={idx === items.length - 1} className="text-gray-500 hover:text-blue-600 disabled:opacity-30">
                        <ChevronRight className="h-4 w-4 rotate-90" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {currentQuestion.question_type === 'matching' && (() => {
            const current = (selectedAnswers[currentQuestion.id] && typeof selectedAnswers[currentQuestion.id] === 'object')
              ? selectedAnswers[currentQuestion.id]
              : {};
            const lefts = currentQuestion.matching_lefts || [];
            const options = currentQuestion.matching_options || [];
            return (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 mb-2">Associe cada item da coluna A à opção correta.</p>
                {lefts.map((left, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <span className="flex-1 p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">{left}</span>
                    <span className="text-gray-400">→</span>
                    <select
                      className="flex-1 p-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                      value={current[left] || ''}
                      onChange={(e) => handleAnswerSelect({ ...current, [left]: e.target.value })}
                    >
                      <option value="">Selecione...</option>
                      {options.map((opt, oi) => (
                        <option key={oi} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={handlePreviousQuestion}
          disabled={currentQuestionIndex === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>

        <div className="flex space-x-2">
          {!isLastQuestion ? (
            <Button
              onClick={handleNextQuestion}
              disabled={!isCurrentAnswered}
            >
              Próxima
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleFinishQuiz}
              className="bg-green-600 hover:bg-green-700"
              disabled={!isCurrentAnswered}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Finalizar Quiz
            </Button>
          )}
        </div>
      </div>

      {/* Question Navigation Dots */}
      <div className="flex justify-center mt-6 space-x-2">
        {quizData.questions.map((_, index) => (
          <button
            key={index}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentQuestionIndex
                ? 'bg-blue-500'
                : selectedAnswers[quizData.questions[index].id]
                ? 'bg-green-500'
                : 'bg-gray-300'
            }`}
            onClick={() => setCurrentQuestionIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

export { CoursePlayer };
export default LMSPage;
